import * as THREE from 'three';
import { GameLoop } from './core/loop';
import { InputSystem } from './core/input';
import { GamepadInput } from './core/gamepad';
import { OrbitCamera, MAX_FOLLOW_RATE } from './core/camera';
import { getInitialQuality, getQualitySettings, type QualityTier } from './core/quality';
import { loadGLB, instantiate } from './core/assets';
import { Block } from './world/block';
import { NavGrid } from './world/navgrid';
import { PlayerCapsule, PLAYER_RADIUS } from './world/controller';
import {
  SPAWN_POSITION,
  BODEGA_EXTERIOR_STANDPOINT,
  isAtBodegaDoorFace,
  LAUNDROMAT_EXTERIOR_STANDPOINT,
  isAtLaundromatDoorFace,
  BASEMENT_HATCH,
  ROOFTOP_GAP_MAX_REACH,
  ROOFTOP_GAP_ACTUAL,
  ROOFTOP_VANTAGE_REQUIRES_JUMP,
  ROOFTOP_VANTAGE_CLEARANCE_MARGIN,
  EL_STATION_COLLECTIBLE_POS,
} from './world/layout';
import { CheckpointTracker } from './world/checkpoints';
import { InteractSystem, type Interactable } from './world/interact';
import { INTERIOR_SPAWN, INTERIOR_ORIGIN, INTERIOR_CAMERA_MAX_DISTANCE, interiorCounterCollider, isPastDoorwayThreshold } from './world/interior';
import { LAUNDROMAT_SPAWN, LAUNDROMAT_ORIGIN, LAUNDROMAT_CAMERA_MAX_DISTANCE, isPastLaundromatDoorwayThreshold } from './world/interiorLaundromat';
import { BASEMENT_SPAWN, BASEMENT_ORIGIN, BASEMENT_CAMERA_MAX_DISTANCE } from './world/interiorBasement';
import { MothSwarm } from './world/ambience';
import { DevOverlay } from './dev/overlay';
import { EnemyManager } from './ai/spawner';
import { Boss, HUBCAP_DAMAGE, HUBCAP_SPEED, HUBCAP_RADIUS, HUBCAP_COLOR, HUBCAP_LAUNCH_HEIGHT, HUBCAP_BREAK_HP } from './ai/boss';
import {
  BossArena,
  GRIDLOCK_ARENA,
  DEN_FIRST_CLEAR_GRIDLOCK,
  DEN_FIRST_CLEAR_RATKING,
  DEN_FIRST_CLEAR_SEWER,
  GRIDLOCK_WRECKS,
  GRIDLOCK_BARRIER_NORTH,
  GRIDLOCK_BARRIER_SOUTH,
  isOnGridlockWreckDeck,
} from './world/arena';
import { LairEncounter, SewerDenEncounter } from './world/dens';
import type { RatKingBoss } from './ai/ratking';
import {
  resolveProjectileHitsOnRatKing,
  resolveMeleeHitsOnRatKing,
  applySuperBlastOnRatKing,
  resolveRatKingMeleeOnPlayer,
  resolveRatKingChargeOnPlayer,
} from './combat/ratkingResolve';
import {
  LAIR_ORIGIN,
  LAIR_SPAWN,
  LAIR_CAMERA_MAX_DISTANCE,
  TUNNEL_BREACH_CENTER,
  TUNNEL_BREACH_STANDPOINT,
  LAIR_ENTRANCE_MARKER,
  LAIR_PILLARS,
  isAtTunnelBreachFace,
  isPastLairDoorwayThreshold,
} from './world/denLair';
import {
  SEWER_ORIGIN,
  PIPE_ENTRY,
  SEWER_PIPE_CAMERA_MAX_DISTANCE,
  SEWER_CHAMBER_CAMERA_MAX_DISTANCE,
  TRAP_HOLE_POS,
  TRAP_HOLE_STANDPOINT,
  isAtTrapHoleFace,
  isPushingTowardTrapHole,
  isPastPipeEntryThreshold,
  isInSewerPipe,
} from './world/denSewer';
import { ProjectilePool, type Projectile } from './combat/projectiles';
import { WeaponSystem, WEAPON_ORDER, type WeaponId } from './combat/weapon';
import { AIM_PRESETS, resolveAimDirection, type AimPreset } from './combat/autoaim';
import { SuperMeter, SUPER_CONSTANTS } from './combat/super';
import { createHealth, tickHealth, tickHitstop, FLASH_DURATION } from './combat/damage';
import {
  resolveProjectileHitsOnEnemies,
  resolveProjectileDamageOnPlayer,
  resolveMeleeAttacksOnPlayer,
  resolveMeleePlayerHits,
  applySuperBlast,
  computeLobVelocity,
  LOB_GRAVITY,
} from './combat/resolve';
import { resolveProjectileHitsOnBoss, resolveMeleeHitsOnBoss, applySuperBlastOnBoss, resolveBossShockwaveOnPlayer, resolveProjectileHitsOnBreakableProjectiles } from './combat/bossResolve';
import { resolveBossKill, type KillLootContext } from './combat/kill';
import { resolveGrazeOnPlayer } from './combat/graze';
import { PickupPool, pickupTintFor } from './loot/pickup';
import { applyCollectedLoot } from './loot/collect';
import type { LootKind } from './loot/droptable';
import { createWallet, grantCoins, grantMaterial, spendCoins, spendMaterial } from './economy/wallet';
import { attemptNextCraftTier, RIOT_PLATING_COST_PARTS, RIOT_PLATING_COST_COINS, RIOT_PLATING_BONUS_HP, SALVAGE_EXCHANGE_COST_PARTS, SALVAGE_EXCHANGE_GAIN_COINS, SALVAGE_EXCHANGE_GAIN_SCRAP } from './economy/craft';
import { WEARABLES, WEARABLE_ORDER, type WearableId } from './economy/wardrobe';
import { RARITY_LABEL, RARITY_ORDER } from './economy/rarity';
import { rollEnemyExtras, rollBossExtras, __resetDropsRngForTest } from './economy/drops';
import { PlayerVisual } from './world/playerVisual';
import { Hud } from './ui/hud';
import { loadSave, salvageSave, writeSave, type SaveData } from './meta/save';
import { AudioBuses } from './audio/buses';
import { playSfx, playPickupArpeggio } from './audio/sfx';
import { MusicSequencer } from './audio/music';
import { PauseMenu, type UiState } from './ui/pause';
import { HuntBoard } from './ui/huntboard';
import { WardrobeScreen } from './ui/wardrobe';
import { StashScreen, type SessionLog } from './ui/stash';
import { buildTasks, nextUndoneTask } from './meta/tasks';
import { STARTER_HUNT, labelFor as huntLabelFor } from './meta/hunts';
import type { Settings, ActiveHunt } from './meta/save';
import type { EnemyKind } from './ai/archetypes';
import { ARCHETYPES } from './ai/archetypes';
import type { Enemy } from './ai/enemy';

const LOOK_SENSITIVITY = 0.0035; // rad per drag-pixel

const params = new URLSearchParams(location.search);
const devMode = params.get('dev') === '1';

const canvas = document.getElementById('scene') as HTMLCanvasElement;
const appEl = document.getElementById('app') as HTMLDivElement;

let quality = getInitialQuality();
const renderer = new THREE.WebGLRenderer({ canvas, antialias: quality.antialias });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.maxPixelRatio));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.NoToneMapping;

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x1a1a1f);

const hemi = new THREE.HemisphereLight(0xcfd8ff, 0x4a4a52, 1.1);
const sun = new THREE.DirectionalLight(0xfff2df, 1.6);
sun.position.set(6, 10, 4);
scene.add(hemi, sun);

const block = new Block();
scene.add(block.group);
// Baked once from the block's own colliders (S03, PLAN-CITY §5) — ground
// enemies path against this instead of S02's direct-seek-only movement.
const navGrid = new NavGrid(block.physics);

// S11: loaded here — before the player's very first teleport, not down by
// the weapon/aim-preset setup like every earlier session left it — because
// the first-run flow immediately below needs `save.progress.hasPlayedBefore`
// to decide WHERE that first teleport lands. Nothing between here and the
// old load site read `save`, so hoisting it is behavior-neutral for every
// other consumer; every later reference in this file is still this exact
// object (loadSave() is never called a second time).
const save: SaveData = loadSave();

const player = new PlayerCapsule(block.physics);
player.addTo(scene);

// Wave L-2 (PLAN-CITY2 §6): the REAL player visual (chr_runner.glb, idle/
// walk/run/jump/slide) — see world/playerVisual.ts's own header comment for
// the full story. `player.mesh` (the capsule) stays the actual collision body
// forever; once the GLB resolves, tick() hides the capsule's OWN visible
// mesh (not `player` itself, and not the shadow blob) so only one body ever
// renders. Constructed here, not lazily, so its `group` exists in the scene
// graph from frame 1 even while the async load is still in flight.
const playerVisual = new PlayerVisual(scene, loadGLB);
let playerVisualCapsuleHidden = false;

// --- S11: first-run flow (PLAN-CITY §5: "first-run flow (bodega -> street
// -> guided first hunt, <=8s to action)") ---
// A genuinely fresh save (never true again after this boot, per the flag
// below) spawns the player INSIDE the bodega instead of on the street, with
// a starter hunt already accepted (meta/hunts.ts's fixed STARTER_HUNT, not
// one of the random board picks — a guided first objective shouldn't carry
// board-roll variance) and one welcome toast shown once `hud` exists further
// down. No loading screen, no blocking dialog, no multi-step toast sequence
// to choreograph — the whole flow is two teleport-equivalent writes plus a
// single self-clearing toast, so it adds effectively zero time on top of the
// sub-2-second title->moving boot G1 already verified on Karan's phone
// (STATUS-CITY.md's G1 row) — comfortably inside the <=8s budget by
// construction, not by measuring a new timer.
const isFirstRun = !save.progress.hasPlayedBefore;
let insideBodega = isFirstRun;
// B-2 "Enterable interiors" (PLAN-CITY2 §3): the first-run flow only ever
// spawns inside the bodega (STARTER_HUNT sends the player out the bodega
// door onto the street, not toward either new room) — both new flags start
// false regardless of isFirstRun, and are restored from `save.suspended`
// below like `insideBodega` if a resume applies.
let insideLaundromat = false;
let insideBasement = false;
// Wave D (PLAN-CITY2 §4): same shape as insideLaundromat/insideBasement above.
let insideLair = false;
let insideSewer = false;
if (isFirstRun) {
  player.teleport(INTERIOR_SPAWN.x, INTERIOR_SPAWN.z);
  save.progress.hasPlayedBefore = true;
  save.progress.activeHunt = { kind: STARTER_HUNT.kind, targetCount: STARTER_HUNT.targetCount, killedCount: 0, rewardCoins: STARTER_HUNT.rewardCoins, rewardScrap: STARTER_HUNT.rewardScrap };
  // Written immediately, not left for the periodic snapshot — a crash
  // between this line and the first 4s snapshot must not silently repeat
  // the first-run flow (and hand out a second starter hunt) on next boot,
  // same "don't wait" discipline `hiddenStashFound` already established.
  writeSave(save);
} else {
  player.teleport(SPAWN_POSITION.x, SPAWN_POSITION.z);
}

// --- S02: combat feel slice ---
// Player HP/knockback/i-frames — a generic combat/damage.ts Health, same
// component type every enemy uses, kept OUTSIDE PlayerCapsule (which stays
// movement/physics-only) per §0.7's single-choke-point discipline.
const PLAYER_MAX_HP = 100;
// S11: Scrap Armor (the crafting bench's one real recipe) is a permanent,
// one-time +max-HP unlock — applied here, at construction, from the SAME
// `save.progress.armorCrafted` flag the craft interaction itself sets, so a
// returning player's bonus HP is live from frame 1, not just after their
// next craft-bench visit. `ARMOR_BONUS_HP` is the one shared number both
// this line and the live craft grant (handleInteract's 'craft' case, below)
// read — never hand-copied twice.
const ARMOR_BONUS_HP = 20;
const ARMOR_COST_SCRAP = 4;
const ARMOR_COST_COINS = 25;
const playerHealth = createHealth(PLAYER_MAX_HP + (save.progress.armorCrafted ? ARMOR_BONUS_HP : 0), 0);
let playerDead = false;
let respawnTimer = 0;
const RESPAWN_DELAY_SECONDS = 1.4;
const RESPAWN_INVULN_MS = 1500;

const projectiles = new ProjectilePool();
scene.add(projectiles.group);

const pickups = new PickupPool();
scene.add(pickups.group);
pickups.setPhysics(block.physics); // I-2's unreachable-drop guard — see PickupPool.setPhysics's own comment
// Wave E (PLAN-CITY3 §6, E-3): I-4's exported persistent-collectible
// primitive (loot/pickup.ts's spawnPersistentCollectible), placed once at
// boot — a genuine no-op if `save.progress.placedCollectibles` already
// recorded it found (that method's own comment). id 'station_platform'
// matches loot/droptable.ts's own illustrative example for this exact id.
pickups.spawnPersistentCollectible(
  'station_platform',
  new THREE.Vector3(EL_STATION_COLLECTIBLE_POS.x, EL_STATION_COLLECTIBLE_POS.y, EL_STATION_COLLECTIBLE_POS.z),
  save.progress.placedCollectibles['station_platform'] === true,
);
const wallet = createWallet();

// Wave L-1/L-2 (PLAN-CITY2 §6): preload every C09 wearable the SAME way
// every enemy/boss asset is already preloaded above/below (a `loadGLB(...)
// .then(...)` per id) — cached in `playerVisual` regardless of ownership, so
// the instant a piece IS granted (either from `save.wardrobe.owned` at boot,
// or a fresh drop mid-run) it's already equippable with no extra load wait.
for (const id of WEARABLE_ORDER) {
  loadGLB(`/assets/models/${id}.glb`).then((glb) => {
    playerVisual.cacheWearableAsset(id, glb);
    // Re-apply whatever's currently equipped for this slot — covers the
    // case where `save.wardrobe.equipped` already names this piece (loaded
    // from a prior session) but this async load resolves AFTER the initial
    // equip attempt below ran and no-op'd (not cached yet at that moment).
    const slot = WEARABLES[id].slot;
    if (save.wardrobe.equipped[slot] === id) playerVisual.equip(slot, id);
  });
}
for (const slot of ['head', 'torso', 'back'] as const) {
  const equippedId = save.wardrobe.equipped[slot];
  if (equippedId) playerVisual.equip(slot, equippedId);
}

/**
 * Wave L-1 (PLAN-CITY2 §6): bridges `combat/kill.ts`'s deliberately
 * SaveData-decoupled `KillLootContext` to the real, live `save` object —
 * rebuilt fresh at every kill call site (cheap: a kill is not a per-frame
 * event) rather than cached, so it always reflects the exact pre-kill state
 * (crucial for `everKilledFirstTime`, which the kill choke point itself uses
 * to decide the guaranteed first-kill grant).
 */
function buildLootContext(): KillLootContext {
  return {
    ownedWearables: new Set(Object.keys(save.wardrobe.owned) as WearableId[]),
    everKilledFirstTime: new Set(Object.keys(save.progress.everKilled)),
    grantWearableDirect,
  };
}

/** Called once per kill AFTER the roll already happened (see `buildLootContext`'s own comment) — marks this kind/bossKey as no-longer-first for every future kill. Durable immediately (not left for the periodic snapshot): a crash between this kill and the next 4s snapshot must not let a second kill of the same kind re-roll the guaranteed first-kill cosmetic. */
function markKilledOnce(key: string): void {
  if (save.progress.everKilled[key]) return;
  save.progress.everKilled[key] = true;
  snapshotSuspendedRun();
}

/**
 * Wave L-2: grants a cosmetic — `save.wardrobe.owned` + an auto-equip if
 * that slot is currently empty (a fresh piece should be visible immediately,
 * not silently sit in the wardrobe until the player thinks to check it).
 * Durable immediately, same "don't wait for the periodic snapshot" reasoning
 * as every other one-time grant in this file (`hidden_stash`, `markKilledOnce`
 * above). Data-only — no toast/sfx here; see `grantWearables` below for why.
 * Returns whether it was actually a new grant (false if already owned).
 */
function grantWearable(id: WearableId): boolean {
  if (save.wardrobe.owned[id]) return false; // already owned — `economy/drops.ts`'s own anti-farm cap should make this unreachable, but a cosmetic must never be granted "again" if it somehow is
  save.wardrobe.owned[id] = true;
  const def = WEARABLES[id];
  if (!save.wardrobe.equipped[def.slot]) {
    save.wardrobe.equipped[def.slot] = id;
    playerVisual.equip(def.slot, id); // no-op safely if the asset hasn't cached yet — the preload loop's own `.then()` re-applies it the moment it does
  }
  return true;
}

/**
 * Review fix (LOW, spec-fidelity): `PickupPool.update()` can return several
 * distinct cosmetic pickups collected in ONE physics tick — calling
 * `grantWearable` + its own `hud.showToast` once per id would clobber the
 * earlier toast(s) via `Hud.showToast`'s deliberate "latest wins
 * immediately" semantics (kept unchanged for every other caller — see that
 * method's own comment on why). Granting the whole batch first, THEN
 * showing exactly one combined toast/sfx/snapshot for whatever was actually
 * new, fixes the lost-announcement bug without touching `showToast` itself.
 */
function grantWearables(ids: WearableId[]): void {
  const granted = ids.filter((id) => grantWearable(id));
  if (granted.length === 0) return;
  snapshotSuspendedRun();
  const highestRarity = granted.map((id) => WEARABLES[id]).reduce((a, b) => (RARITY_ORDER.indexOf(b.rarity) > RARITY_ORDER.indexOf(a.rarity) ? b : a));
  const text =
    granted.length === 1
      ? `✨ ${RARITY_LABEL[highestRarity.rarity]} — ${highestRarity.label} (${highestRarity.sourceLabel})!`
      : `✨ ${granted.length} NEW ITEMS — ${granted.map((id) => WEARABLES[id].label).join(', ')}!`;
  // Review fix (MEDIUM, spec-fidelity): PLAN-CITY2 L-4 calls for "a
  // rarity-colored toast," not just the rarity NAME appearing as a word in
  // an otherwise-uncolored string — `rarity` now reaches Hud.showToast's own
  // optional param, which applies the matching `.br3d-rarity-*` CSS class
  // (style.css), the same tint family the wardrobe screen's cards already use.
  // A multi-item toast uses the single highest rarity among the batch.
  hud.showToast(text, highestRarity.rarity);
  playSfx(audioBuses, 'rareDrop');
  for (const id of granted) sessionLog.cosmetics.push(WEARABLES[id].label); // I-3's [THIS RUN] log — the one field that can't be a wallet-delta, so it's pushed here at the single choke point every cosmetic grant already funnels through
}

/**
 * Wave I (L6, PLAN-CITY3 §5): `combat/kill.ts`'s choke point for a
 * GUARANTEED first-kill cosmetic — called instead of spawning a pickup, so
 * the crown/plume is written to the save the instant it's earned (never at
 * the mercy of a full pool or an unreachable drop). Reuses `grantWearables`
 * for the actual grant/toast/sfx/persist (so a same-batch double-grant still
 * safely no-ops via `grantWearable`'s own ownership check) and plays the
 * SAME collect-burst VFX `PickupPool.update()` fires on an ordinary
 * collection — the "pickup as celebration only" phrasing from L6 means the
 * celebration still happens, just without gating the actual grant on a
 * physical walk-to-collect step.
 */
function grantWearableDirect(id: WearableId, origin: THREE.Vector3): void {
  const isNew = !save.wardrobe.owned[id];
  grantWearables([id]);
  // Review fix (MEDIUM, spec-fidelity): raw RARITY_COLOR reproduced the
  // exact legendary/coin-gold collision I-2's PICKUP_RARITY_TINT exists to
  // fix, for precisely the game's two guaranteed-first-kill grants (both
  // legendary) — pickupTintFor is the same resolver PickupPool.spawn() uses
  // for every OTHER pickup mesh.
  if (isNew) pickups.spawnBurst(origin, pickupTintFor(WEARABLES[id].rarity));
}

const enemyManager = new EnemyManager();
scene.add(enemyManager.group);
enemyManager.spawnInitial(); // safe before any GLB resolves — trySpawnSlot retries once each is set below
// S10: generalized from a single hardcoded rat load to one call per manifest
// id ai/archetypes.ts now references (spawner.ts's own `setAsset`/`buildVisual`
// comments have the full story on why this replaced the old `setRatAsset`
// special case) — C03's 4 real assets. `bos_ratking`/a second `enm_rat`
// consumer are handled separately below (Wave D-1: the real `RatKingBoss`,
// not the retired `elite_patrol` roaming Enemy — see ai/habitats.ts's own
// Wave D comment).
loadGLB('/assets/models/enm_rat.glb').then((glb) => enemyManager.setAsset('enm_rat', glb));
loadGLB('/assets/models/enm_trash_lobber.glb').then((glb) => enemyManager.setAsset('enm_trash_lobber', glb));
loadGLB('/assets/models/enm_pizza_rat.glb').then((glb) => enemyManager.setAsset('enm_pizza_rat', glb));
loadGLB('/assets/models/enm_pigeon.glb').then((glb) => enemyManager.setAsset('enm_pigeon', glb));
loadGLB('/assets/models/enm_divepigeon.glb').then((glb) => enemyManager.setAsset('enm_divepigeon', glb));

// S10: moth ambience (PLAN-CITY §1.4) — pure atmosphere, ticked once per
// frame from tick() below, right alongside every other per-frame world
// system.
const mothSwarm = new MothSwarm();
scene.add(mothSwarm.group);

// --- S06: boss framework + Gridlock ---
// `boss` stays null until the GLB resolves (same async pattern as the rat
// above) — the arena/encounter logic below is written to tolerate that,
// same as EnemyManager already tolerates `ratGLB` being unset briefly.
const bossArena = new BossArena(GRIDLOCK_ARENA);
let boss: Boss | null = null;
loadGLB('/assets/models/bos_gridlock.glb').then((glb) => {
  const inst = instantiate(glb);
  boss = new Boss({ group: inst.group, materials: inst.materials, mixer: inst.mixer, clips: inst.clips }, GRIDLOCK_ARENA.centerX, GRIDLOCK_ARENA.centerZ);
  // K-1(d): "the boss hides ... until the threshold is crossed" — Boss owns
  // its own THREE.Group (constructed here, not inside world/block.ts's own
  // scene graph, per ai/boss.ts's own "stay self-contained" reasoning), so
  // this toggles the group's visibility directly rather than threading it
  // through a block.ts-owned wrapper group — same functional idiom as
  // lairGroup/sewerGroup (hidden by default, one place flips it), just
  // scoped to the object that actually owns it. Revealed the instant
  // `bossArena` transitions dormant->active, below.
  boss.group.visible = false;
  scene.add(boss.group);
});
/** K-1(a): the currently in-flight hubcap projectile, if any — tracked so main.ts can tell `Boss.update()`'s `hubcapInFlight` param whether attack2_wait should keep waiting, and so `reportProjectileHits()` can detect when THIS specific one is destroyed by player fire vs. any other enemy projectile. Cleared once it resolves (hit, expired, or broken). */
let activeHubcap: Projectile | null = null;
/** K-1(a): set true the tick `resolveProjectileHitsOnBreakableProjectiles` reports the tracked hubcap was destroyed — read once by the Gridlock tick block below, then cleared alongside `activeHubcap`. */
let hubcapBrokenThisResolve = false;

// --- Wave D-1: the Rat King basement lair (world/denLair.ts, world/dens.ts) ---
// `bos_ratking` now spawns the REAL `RatKingBoss` (ai/ratking.ts, built by
// Wave M/C08 but never live until now — see that class's own header
// comment) inside the isolated lair origin, not the retired roaming
// `elite_patrol` Enemy. `enm_rat` (already loading above for the street
// population) is ALSO fed to `lairEncounter` for the SWARM CALL adds — same
// shared-GLB-source reuse idiom every reskin in this game already uses
// (Classic gotcha #62/#64's family), just consumed by a second, independent
// system rather than `EnemyManager`.
const lairEncounter = new LairEncounter();
loadGLB('/assets/models/enm_rat.glb').then((glb) => lairEncounter.setRatGlb(glb));
loadGLB('/assets/models/bos_ratking.glb').then((glb) => {
  const inst = instantiate(glb);
  const ratKing = lairEncounter.spawnBoss({ group: inst.group, materials: inst.materials, mixer: inst.mixer, clips: inst.clips });
  scene.add(ratKing.group);
  // Fix session (review finding, LOW, corroborated by 2 lenses): the S07
  // resume path's `if (insideLair) lairEncounter.onEnter()` (below, at boot)
  // runs synchronously, long before this async GLB load resolves — so
  // `onEnter()`'s `this.boss?.engage()` was a no-op (`boss` was still null),
  // even though `arena.activate()` still ran. The freshly-constructed boss
  // then starts its FIRST tick at `stateT:0` with `engage()` never having
  // armed `ENGAGE_DELAY` — a player resuming a suspended run inside the lair
  // got attacked with no intro beat. Re-arm here if the arena is ALREADY
  // active by the time the boss finally exists.
  if (lairEncounter.arena.state === 'active') ratKing.engage();
});
// The RED CHARGE intact/cracked pillar swap needs each pillar's own loaded
// GLB instance (`block.getLairPillarGroups()`, populated once `attachDenLair()`
// resolves inside `block.artReady`) fed into `lairEncounter` by index —
// without this, `LairEncounter.registerPillarGroup()` never runs, so the
// pillar-crack flag flips internally but has no mesh to actually toggle.
block.artReady.then(() => {
  block.getLairPillarGroups().forEach((group, i) => lairEncounter.registerPillarGroup(i, group));
});

// --- Wave D-3: the water-hole sewer den (world/denSewer.ts, world/dens.ts) ---
// `sewer_elite` is a shared-asset reskin of the SAME `enm_rat` GLB (ai/
// archetypes.ts's own comment) — a single standalone `Enemy` instance,
// managed by `sewerEncounter`, not a habitat slot (D-3's own "a single
// elite, not a roaming habitat zone" spec).
const sewerEncounter = new SewerDenEncounter();
loadGLB('/assets/models/enm_rat.glb').then((glb) => {
  const archetype = ARCHETYPES.sewer_elite;
  const inst = instantiate(glb, archetype.tint);
  inst.group.scale.setScalar(archetype.scale);
  const elite = sewerEncounter.spawn({ group: inst.group, materials: inst.materials, mixer: inst.mixer, clips: inst.clips, baseScale: archetype.scale });
  scene.add(elite.group);
});

/**
 * The boss, but ONLY once the encounter has genuinely started —
 * `bossArena.state === 'active'`. Self-caught during S06's own
 * verification: without this gate, every damage-in path (bullets/melee/
 * super) checked raw `boss` directly, which stays non-null the INSTANT the
 * GLB loads, long before the player has entered the arena — a player
 * standing outside the 5.5m entry radius but within a weapon's own range
 * (The Nine reaches 16m, Sawed-Off 7m — both exceed the entry radius) could
 * chip away at a visibly "asleep," not-yet-engaged boss with zero feedback
 * (no HP bar, since that's separately gated on the same `active` state),
 * before the fight has even nominally begun. A callable helper, not a
 * hoisted value computed once per tick, so every call site sees whatever
 * `bossArena.state` is AT THE MOMENT it runs — correct even across a
 * same-tick dormant->active transition or an in-progress mid-fight reset.
 */
function engagedBoss(): Boss | null {
  return boss && bossArena.state === 'active' ? boss : null;
}

/**
 * Wave D: the lair's SWARM CALL adds + the sewer den's single elite —
 * merged into the SAME `Enemy[]` combat-resolution calls `enemyManager.
 * getEnemies()` already feeds (melee/super/projectile, below), so kill
 * loot/hunt-tracking/`markKilledOnce` all apply identically with zero new
 * plumbing. Callable at any point in the tick (both encounters are no-ops —
 * empty arrays — until the player has actually walked into their den), a
 * fresh array each call rather than cached, matching this file's own
 * `buildLootContext()` convention for "cheap enough to rebuild on demand."
 */
function denExtraEnemies(): Enemy[] {
  const extra: Enemy[] = lairEncounter.getSwarmRats();
  if (sewerEncounter.elite && sewerEncounter.arena.state === 'active') extra.push(sewerEncounter.elite);
  return extra;
}

// S07: the unified versioned save (meta/save.ts) — loaded once, up near the
// top of this file (S11 moved the load itself earlier so the first-run flow
// could read it before the player's very first teleport; this comment stays
// here as the definitive note for everything below). Every reference to
// `save.settings.*`/`save.progress.*` in this file reads/writes that SAME
// object — `loadSave()` itself is called exactly once, up top.
const weapon = new WeaponSystem(save.settings.weaponId);
function setWeaponAndPersist(id: WeaponId): void {
  weapon.setWeapon(id);
  save.settings.weaponId = id;
  writeSave(save);
}

const superMeter = new SuperMeter();
let aimPreset: AimPreset = save.settings.aimPreset;
function setAimPresetAndPersist(p: AimPreset): void {
  aimPreset = p;
  save.settings.aimPreset = p;
  writeSave(save);
}

const hud = new Hud();
if (isFirstRun) {
  hud.showToast('🏪 Welcome to Astoria — step outside. First hunt: 3 Rats, in the alley.');
}

// --- S05: world systems (checkpoints, interact prompts, bodega interior) ---
const checkpoints = new CheckpointTracker();
const interact = new InteractSystem();

// --- S07: audio buses + chiptune sequencer (PLAN-CITY §5) ---
const audioBuses = new AudioBuses();
audioBuses.setMusicVolume(save.settings.musicVolume);
audioBuses.setSfxVolume(save.settings.sfxVolume);
const music = new MusicSequencer(audioBuses);
// Browsers refuse to start an AudioContext before a real user gesture —
// this game already requires a pointerdown/keydown to do anything, so
// unlock (and start the Astoria motif) on the first of either. Both calls
// are idempotent (AudioBuses.unlock/MusicSequencer.start each no-op past
// their first success), so having both listeners armed and letting
// whichever fires first win is safe — no manual removeEventListener needed.
// Review finding (MEDIUM): a `pointerdown`/`keydown` reaching this listener
// requires the event to actually bubble all the way to `window` — but
// PauseMenu's own buttons (pause/RESUME/SETTINGS/BACK/every settings
// toggle) and JumpButton all call `stopPropagation()` in their own
// handlers, so a player whose FIRST touch lands on any of them (a fully
// plausible first interaction — checking Settings before ever moving) never
// reaches this listener at all, silently delaying audio unlock past the
// "first real gesture" this comment promises. Fixed by listening on the
// CAPTURE phase instead of the default bubble phase: capture fires
// top-down (window first, before the event ever reaches the target
// element), so it has already run by the time any target's own
// `stopPropagation()` call could matter — nothing downstream can prevent a
// capture-phase listener on the OUTERMOST ancestor from having already
// fired.
function unlockAudioOnce(): void {
  audioBuses.unlock();
  music.start();
}
window.addEventListener('pointerdown', unlockAudioOnce, { once: true, capture: true });
window.addEventListener('keydown', unlockAudioOnce, { once: true, capture: true });

// The bodega interior is far smaller than the outdoor scale OrbitCamera's
// default distance/pitch were tuned for — at the default 5.5m pull-back the
// occlusion ray always climbs above the room's 2.6m walls before reaching
// one, landing the camera outside/above the room every time (S05 review,
// live-reproduced). `insideBodega` gates a smaller `maxDistance`
// (world/interior.ts's INTERIOR_CAMERA_MAX_DISTANCE, derived from the
// room's own height) on every orbitCam.update()/snapTo() call —
// `activeCameraMaxDistance()` is the one place that decision is made, so
// every call site stays consistent by construction rather than needing the
// same ternary hand-copied at each one. `insideBodega` itself is declared
// up top now (S11 — the first-run flow needs to set it before this file's
// FIRST orbitCam.snapTo() call, further down), initialized there from
// `isFirstRun` rather than a hardcoded `false`; the resume block right below
// (S07 review fix) still sets it directly when resuming into the interior,
// exactly as before.
// B-2: extended to the two new rooms, same shape — each has its own smaller
// max-distance constant (interiorLaundromat.ts/interiorBasement.ts), derived
// the same way INTERIOR_CAMERA_MAX_DISTANCE was for its own room's real
// dimensions. At most one of the three flags is ever true (each enter*
// function below clears the other two), so the if/else-if order here never
// actually has to arbitrate a real conflict — it's just the cheapest correct
// shape for "return whichever room's constant applies, if any."
function activeCameraMaxDistance(): number | undefined {
  if (insideBodega) return INTERIOR_CAMERA_MAX_DISTANCE;
  if (insideLaundromat) return LAUNDROMAT_CAMERA_MAX_DISTANCE;
  if (insideBasement) return BASEMENT_CAMERA_MAX_DISTANCE;
  // Wave D-1: the lair's own 18x14m floor/6.0m ceiling comfortably clears
  // every outdoor zoom preset (world/denLair.ts's LAIR_CAMERA_MAX_DISTANCE
  // comment) — LAIR_CAMERA_MAX_DISTANCE is `undefined` on purpose, so this
  // branch is a no-op that documents the decision rather than silently
  // falling through to it.
  if (insideLair) return LAIR_CAMERA_MAX_DISTANCE;
  // Wave D-3: the sewer den needs its OWN tighter clamp inside the 2.2m-clear
  // pipe bore than inside the 12x10m/3.5m chamber — picked dynamically off
  // the player's own live Z, not a single fixed room-wide constant.
  if (insideSewer) return isInSewerPipe(player.position.z) ? SEWER_PIPE_CAMERA_MAX_DISTANCE : SEWER_CHAMBER_CAMERA_MAX_DISTANCE;
  return undefined; // undefined -> orbitCam's own DISTANCE default (JS default params trigger on undefined, not just omission)
}

// --- S07: resume from a suspended snapshot (meta/save.ts) ---
// Applied once at boot, right here — after player/wallet/checkpoints all
// exist but BEFORE orbitCam is constructed below, so orbitCam's own initial
// snapTo() naturally frames the RESUMED position with no second snap call
// needed. A no-op when there's no valid suspended snapshot (a first-ever
// boot, or one salvageSave's own validation dropped) — the SPAWN_POSITION
// teleport already above put the player somewhere sane either way. This
// game has no discrete levels yet, so "resume" means genuine continuity
// across a reload (position/checkpoint/hp/wallet), not Classic's
// mid-boss-fight snapshot — see SuspendedRun's own doc comment.
if (save.suspended) {
  const susp = save.suspended;
  player.teleport(susp.playerX, susp.playerZ);
  checkpoints.restoreDiscovered(susp.checkpointId);
  playerHealth.hp = Math.min(playerHealth.maxHp, Math.max(1, susp.hp));
  // Review finding (MEDIUM): economy/wallet.ts's own grantCoins doc comment
  // is explicit — "no call site outside this module may write wallet.coins
  // directly." A direct assignment here silently bypassed that choke point
  // (harmless today since the values are already clamped/validated by
  // salvageSave, but a future cap/telemetry/UI hook added inside
  // grantCoins/grantMaterial would silently never fire on resume). `wallet`
  // was just freshly created at 0/0 a few lines above, so granting the
  // saved amount onto that clean baseline reaches the identical absolute
  // value the old direct assignment did.
  grantCoins(wallet, susp.coins);
  grantMaterial(wallet, 'scrap', susp.scrap);
  grantMaterial(wallet, 'part', susp.part); // Wave L-1/L-3 review fix (HIGH) — the missing half of snapshotSuspendedRun's own fix above
  // Review finding (HIGH, confirmed independently by 2 review lenses): a
  // snapshot taken while inside the bodega restores playerX/playerZ to
  // interior coordinates, but without this line `insideBodega` stays at its
  // hardcoded `false` default — activeCameraMaxDistance() then resolves the
  // OUTDOOR pull-back inside the tiny interior room, reproducing the exact
  // S05 camera-occlusion bug a real door-entry already fixed once.
  insideBodega = susp.insideBodega;
  // B-2: same reasoning as insideBodega right above — a snapshot taken
  // inside the laundromat/basement must restore that flag too, or
  // activeCameraMaxDistance() resolves the OUTDOOR pull-back inside
  // whichever tiny room the player actually resumes into.
  insideLaundromat = susp.insideLaundromat;
  insideBasement = susp.insideBasement;
  // Wave D: same reasoning, same requirement, two more rooms.
  insideLair = susp.insideLair;
  insideSewer = susp.insideSewer;
}

/**
 * Wave I (I-3): "[THIS RUN] coalesced session pickup log." Captured HERE —
 * after the resume block above has already restored wallet/huntsCompleted
 * to their true post-resume values — so a resumed session's own log starts
 * counting from THIS session's own gains, not the whole save's lifetime
 * total. `coins`/`scrap`/`part`/`huntsCompleted` are plain deltas read live
 * off `wallet`/`save.progress` (see `getSessionLog` below) rather than
 * incremented at every scattered grant call site — `economy/wallet.ts`'s
 * own `grantCoins`/`grantMaterial` choke points already guarantee nothing
 * can move those numbers without this delta seeing it. `hearts`/`cosmetics`
 * genuinely need their own increments (see the loot-collection block and
 * `grantWearables` respectively) since neither is a simple running total.
 */
const sessionStart = { coins: wallet.coins, scrap: wallet.materials.scrap, part: wallet.materials.part, huntsCompleted: save.progress.huntsCompleted };
const sessionLog: SessionLog = { coins: 0, scrap: 0, part: 0, hearts: 0, huntsCompleted: 0, cosmetics: [] };
function getSessionLog(): SessionLog {
  sessionLog.coins = wallet.coins - sessionStart.coins;
  sessionLog.scrap = wallet.materials.scrap - sessionStart.scrap;
  sessionLog.part = wallet.materials.part - sessionStart.part;
  sessionLog.huntsCompleted = save.progress.huntsCompleted - sessionStart.huntsCompleted;
  return sessionLog;
}

/**
 * Review-confirmed inversion (MEDIUM, spec-fidelity): a yaw-preserving door
 * cut turns a held stick into "march straight back into the door you just
 * came through", because camera-relative input keeps its world direction
 * across any cut that doesn't touch yaw — and on ENTER it walked the player
 * from INTERIOR_SPAWN back over the walk-out threshold in ~0.3s (a
 * revolving door). The two doors get different treatments:
 *
 * ENTER — absolute yaw=0 (forward = world -z, facing the counter): the
 * canonical vestibule framing, and the common approach (walking +z at the
 * exterior door) continues naturally into the room. The rare back-into-the-
 * door entry (button OR the follow-up-2 walk-in — a held backward stick can
 * legitimately satisfy the walk-in's intent gate while the camera faces the
 * street) bounces straight back out once via the exit flip below and then
 * continues away — self-resolving, never a loop (verification-round
 * confirmed: after the exit flip, the same held stick maps to
 * away-from-the-store, so the cycle terminates in one bounce).
 *
 * EXIT — RELATIVE flip (current yaw + π), not an absolute: rotating the
 * basis by π negates ANY held stick's world output, and the pre-cut output
 * is by definition "through the doorway (+z)" — so whatever input carried
 * the player out keeps carrying them away from the storefront, for every
 * input shape (live-verified: an absolute exit yaw broke the primary
 * hold-backward walk-out, marching the player back into the door).
 */
const BODEGA_ENTER_FACING_YAW = 0;

/**
 * T3-1 follow-up review fix (LOW): after an AUTO door transition lands the
 * player inside the OTHER side's own interactable trigger (the walk-out
 * exit lands at bodega_enter's exact center; the walk-in enter lands
 * inside bodega_exit's radius), a USE tap already in flight (latched after
 * the crossing tick's consume) would execute on the NEXT tick against that
 * new nearest interactable and teleport the player straight back through
 * the door. Presses are still drained every tick (S02's always-drain law)
 * — this timer only gates ACTING on them briefly, and only after the AUTO
 * transitions (the button paths keep today's exact behavior).
 */
const INTERACT_GRACE_AFTER_AUTO_TRANSITION = 0.3;
let interactGraceTimer = 0;


/**
 * The one true bodega-exit routine — the EXIT BODEGA button and the
 * walk-out-the-doorway threshold (tick()'s `isPastDoorwayThreshold` check,
 * T3-1 follow-up) both land here, so the two paths can never drift apart
 * (Classic gotcha #19's formula-drift family, applied to a state
 * transition).
 */
function exitBodegaToStreet(): void {
  insideBodega = false;
  block.setInteriorVisible(false); // T3-1
  player.teleport(BODEGA_EXTERIOR_STANDPOINT.x, BODEGA_EXTERIOR_STANDPOINT.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), orbitCam.getYaw() + Math.PI); // relative flip — see BODEGA_ENTER_FACING_YAW's comment
}

/**
 * The one true bodega-enter routine — the ENTER BODEGA button and the
 * walk-into-the-door-face trigger (tick()'s `isAtBodegaDoorFace` check,
 * T3-1 follow-up 2: "once i leave i can't get back inside") both land
 * here, mirroring exitBodegaToStreet() exactly.
 */
function enterBodega(): void {
  insideBodega = true;
  block.setInteriorVisible(true); // T3-1: the interior room only renders while actually inside it
  player.teleport(INTERIOR_SPAWN.x, INTERIOR_SPAWN.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), BODEGA_ENTER_FACING_YAW); // cut, don't slide the camera across the map (matches every other teleport in this file)
}

/**
 * B-2 "Enterable interiors" (PLAN-CITY2 §3) — the laundromat's own enter/
 * exit pair, mirroring `enterBodega()`/`exitBodegaToStreet()` exactly
 * (same absolute-yaw-0 entry / relative-π-flip exit convention — both rooms'
 * door face the same way, south wall, entering north into the room, so the
 * identical yaw values apply unchanged, not re-derived). No defensive
 * clearing of `insideBodega`/`insideBasement` here: the self-healing
 * invariant in tick() already recovers any stale flag within one tick,
 * path-agnostic, matching this file's own stated preference (`insideBodega`
 * self-heal comment: "beats hooking every individual teleport call site by
 * name") over hand-clearing every sibling flag at every entry point.
 */
function exitLaundromatToStreet(): void {
  insideLaundromat = false;
  block.setLaundromatVisible(false);
  player.teleport(LAUNDROMAT_EXTERIOR_STANDPOINT.x, LAUNDROMAT_EXTERIOR_STANDPOINT.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), orbitCam.getYaw() + Math.PI);
}

function enterLaundromat(): void {
  insideLaundromat = true;
  block.setLaundromatVisible(true);
  player.teleport(LAUNDROMAT_SPAWN.x, LAUNDROMAT_SPAWN.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), BODEGA_ENTER_FACING_YAW);
}

/**
 * B-2: the basement vestibule's enter/exit pair — button-only both ways (see
 * interiorBasement.ts's own header comment for why this room has no
 * walk-through door/threshold machinery), so neither function needs the
 * bodega/laundromat pair's yaw-flip trick: there's no held-stick "walked
 * through a cut" concern when the transition only ever fires from a discrete
 * button press. A fixed yaw of 0 for both is an arbitrary, purely cosmetic
 * choice for this small square room.
 */
function exitBasementToTunnel(): void {
  insideBasement = false;
  block.setBasementVisible(false);
  player.teleport(BASEMENT_HATCH.x, BASEMENT_HATCH.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), 0);
}

function enterBasement(): void {
  insideBasement = true;
  block.setBasementVisible(true);
  player.teleport(BASEMENT_SPAWN.x, BASEMENT_SPAWN.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), 0);
}

/**
 * Wave D-1: the lair's own walk-out/walk-in pair — T3's door law, mirroring
 * `exitLaundromatToStreet()`/`enterLaundromat()` exactly (absolute yaw-0
 * entry / relative-π-flip exit). Leaving mid-fight also resets the encounter
 * (D-4's own rule) via `lairEncounter.onExit()`.
 *
 * Fix session (review finding, corroborated by 2 lenses): `hud.hideBossBar()`
 * was missing here — Gridlock's own mid-fight reset (line ~1521) calls it for
 * exactly this reason, but the lair's port dropped it, so walking out mid-fight
 * left "THE RAT KING hp/maxHp" frozen on screen for the rest of the session,
 * over ordinary open-world play, until an unrelated Gridlock fight happened to
 * hide it.
 */
function exitLairToTunnel(): void {
  insideLair = false;
  block.setLairVisible(false);
  lairEncounter.onExit();
  hud.hideBossBar();
  player.teleport(TUNNEL_BREACH_STANDPOINT.x, TUNNEL_BREACH_STANDPOINT.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), orbitCam.getYaw() + Math.PI);
}

function enterLair(): void {
  insideLair = true;
  block.setLairVisible(true);
  // K-2: "arrival beat ('THE RAT KING' toast + boss bar one-shot — today a
  // comment claims a toast that doesn't exist)" — the comment three lines
  // below this one (on the tick-loop's own showBossBar call) always claimed
  // this fired; it never did. Checked BEFORE calling onEnter() (which flips
  // dormant->active) so this only fires on a genuine fresh arrival, not
  // every walk-in while already engaged/exposed mid-fight.
  const isFreshArrival = lairEncounter.arena.state === 'dormant';
  lairEncounter.onEnter();
  if (isFreshArrival) hud.showToast('THE RAT KING');
  player.teleport(LAIR_SPAWN.x, LAIR_SPAWN.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), BODEGA_ENTER_FACING_YAW);
}

/**
 * Wave D-3: the sewer den's own walk-out/walk-in pair, same T3 door-law shape.
 * Fix session: teleports to `TRAP_HOLE_STANDPOINT` (offset south of the
 * trigger ring), not `TRAP_HOLE_POS` itself — landing dead-center on the
 * entry trigger risked an immediate re-entry loop on the very next held
 * input (see that constant's own comment).
 */
function exitSewerToStreet(): void {
  insideSewer = false;
  block.setSewerVisible(false);
  sewerEncounter.onExit();
  player.teleport(TRAP_HOLE_STANDPOINT.x, TRAP_HOLE_STANDPOINT.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), orbitCam.getYaw() + Math.PI);
}

function enterSewer(): void {
  insideSewer = true;
  block.setSewerVisible(true);
  sewerEncounter.onEnter();
  player.teleport(PIPE_ENTRY.x, PIPE_ENTRY.z);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance(), BODEGA_ENTER_FACING_YAW);
}

function handleInteract(target: Interactable): void {
  switch (target.action) {
    case 'enter_bodega':
      enterBodega();
      break;
    case 'exit_bodega':
      exitBodegaToStreet();
      break;
    case 'locked_seam':
      hud.showToast(target.lockedMessage ?? 'LOCKED');
      break;
    case 'heal':
      if (playerHealth.hp < playerHealth.maxHp) {
        playerHealth.hp = playerHealth.maxHp;
        hud.showToast('❤️ HEALED UP');
        playSfx(audioBuses, 'checkpoint');
      } else {
        hud.showToast('Already at full health.');
      }
      break;
    case 'stash':
      // Wave I (I-3): used to just toast the raw wallet numbers — now opens
      // the real THE STASH screen (ui/stash.ts), the same field-accessible
      // route the third pause-menu button also offers.
      setUiState('stash');
      break;
    case 'craft':
      // S11's one real recipe (PLAN-CITY §5): Scrap Armor, a permanent,
      // one-time +ARMOR_BONUS_HP max-HP unlock. Both costs are checked
      // BEFORE either is spent — `spendMaterial`/`spendCoins` each already
      // refuse individually if underfunded, but checking both up front
      // avoids ever spending one resource only to fail affording the other.
      if (!save.progress.armorCrafted) {
        if (wallet.materials.scrap < ARMOR_COST_SCRAP || wallet.coins < ARMOR_COST_COINS) {
          hud.showToast(`🔨 SCRAP ARMOR — need ${ARMOR_COST_SCRAP} scrap + ${ARMOR_COST_COINS} coins`);
        } else {
          spendMaterial(wallet, 'scrap', ARMOR_COST_SCRAP);
          spendCoins(wallet, ARMOR_COST_COINS);
          playerHealth.maxHp += ARMOR_BONUS_HP;
          playerHealth.hp = Math.min(playerHealth.maxHp, playerHealth.hp + ARMOR_BONUS_HP);
          save.progress.armorCrafted = true;
          snapshotSuspendedRun(); // same "durable, don't wait for the periodic snapshot" discipline as hidden_stash below
          hud.showToast(`🔨 SCRAP ARMOR CRAFTED — +${ARMOR_BONUS_HP} MAX HP`);
          playSfx(audioBuses, 'craft'); // Wave I (I-1): a dedicated cue, was the same flat 'pickup' blip every other one-time grant here used too
        }
      } else {
        // Wave L-3 (PLAN-CITY2 §6): the bench's next tier(s) — Riot Plating,
        // then Salvage Exchange forever after — same one-interactable,
        // sequential-recipe shape economy/craft.ts's own header comment
        // describes. `attemptNextCraftTier` mutates wallet/playerHealth/
        // progress directly and returns a toast; `changed` gates the
        // snapshot the same way every other one-time reward here does.
        const result = attemptNextCraftTier(save.progress, wallet, playerHealth);
        if (result.changed) {
          snapshotSuspendedRun();
          playSfx(audioBuses, 'craft'); // Wave I (I-1)
        }
        hud.showToast(result.toast);
      }
      break;
    case 'wardrobe':
      setUiState('wardrobe');
      break;
    case 'enter_lair':
      enterLair();
      break;
    case 'enter_sewer':
      enterSewer();
      break;
    case 'hunt_board':
      setUiState('huntboard');
      break;
    case 'hidden_stash':
      if (!save.progress.hiddenStashFound) {
        // Review finding (CONFIRMED): the ORIGINAL order (write the
        // found-flag, THEN grant the wallet) meant a crash/kill in the gap
        // before the next periodic snapshot could permanently lose the
        // reward while the flag still correctly blocked re-claiming it —
        // worse than an ordinary coin-loss risk, since this one has no way
        // to ever recover (unlike a missed kill's coins, where the player
        // can just kill something else). Fixed by granting FIRST, then
        // calling `snapshotSuspendedRun()` (not a bare `writeSave`) so the
        // exact same write captures the found-flag AND the just-granted
        // wallet values atomically — both durable or neither is, never one
        // without the other.
        grantCoins(wallet, 40);
        grantMaterial(wallet, 'scrap', 3);
        save.progress.hiddenStashFound = true;
        snapshotSuspendedRun();
        hud.showToast('🎒 HIDDEN STASH — +40 COINS, +3 SCRAP');
        playSfx(audioBuses, 'stash'); // Wave I (I-1)
      } else {
        hud.showToast('Already searched — empty.');
      }
      break;
    case 'enter_laundromat':
      enterLaundromat();
      break;
    case 'exit_laundromat':
      exitLaundromatToStreet();
      break;
    case 'laundromat_stash':
      // B-2: same one-time-reward + grant-then-snapshot-atomically pattern as
      // `hidden_stash` above (that case's own comment has the full "why
      // grant before the flag write" story).
      if (!save.progress.laundromatStashFound) {
        grantCoins(wallet, 25);
        grantMaterial(wallet, 'scrap', 2);
        save.progress.laundromatStashFound = true;
        snapshotSuspendedRun();
        hud.showToast('🧺 SPARE CHANGE IN THE DRYER — +25 COINS, +2 SCRAP');
        playSfx(audioBuses, 'stash'); // Wave I (I-1)
      } else {
        hud.showToast('Already checked every machine.');
      }
      break;
    case 'enter_basement':
      enterBasement();
      break;
    case 'exit_basement':
      exitBasementToTunnel();
      break;
    case 'rooftop_vantage':
      // B-1 review finding (CONFIRMED, spec-fidelity): `InteractSystem`'s own
      // `requireY` (world/interact.ts) only keeps the ON-SCREEN PROMPT from
      // showing far below the platform — it can't distinguish "genuinely
      // standing on top" from "airborne this exact height mid-jump," since
      // the tower's 1.2m rise was deliberately tuned to sit just under a
      // standing jump's real apex, so ANY jump near the tower carries the
      // player's feet above its 13.2m top for a real ~0.29s window (during
      // which collision.ts's own vertical-clearance rule makes the tower's
      // wall inert, per that rule's own comment — the player can freely
      // drift into its XZ column while airborne). A player who timed an
      // interact-press during that window, without ever actually landing,
      // would otherwise claim this "roof-only" reward from mid-air. Gated
      // here with the real signal `requireY` can't check: `player.state.
      // grounded`. Checking `grounded` ALONE (not also re-comparing Y here)
      // is enough: this case only ever runs after `requireY` already
      // narrowed the player's Y to within 0.6m of the tower's 13.2m top, and
      // no OTHER floor region exists in that band at this XZ column — so
      // "grounded" + "already Y-filtered to get here" can only mean standing
      // on the tower itself. A no-op with no toast on failure, not an error
      // message — this can only happen via an airborne press during a brief
      // transit window, not a real "you can't do that here" moment a normal
      // player claiming the reward by actually landing would ever see.
      if (!player.state.grounded) break;
      // B-1: same one-time-reward + grant-then-snapshot-atomically pattern as
      // `hidden_stash` above (that case's own comment has the full "why
      // grant before the flag write" story).
      if (!save.progress.rooftopVantageFound) {
        grantCoins(wallet, 30);
        grantMaterial(wallet, 'scrap', 2);
        save.progress.rooftopVantageFound = true;
        snapshotSuspendedRun();
        hud.showToast('🏙️ ROOFTOP VIEW — +30 COINS, +2 SCRAP');
        playSfx(audioBuses, 'stash'); // Wave I (I-1)
      } else {
        hud.showToast("You've already taken in the view.");
      }
      break;
    case 'el_platform_vantage':
      // E-3 (PLAN-CITY3 §6): a flavor-only vantage moment tying into E-1's
      // own "Corona continuation" dressing. Deliberately no economy grant
      // and no persisted one-time flag, unlike `rooftop_vantage` above —
      // `save.progress`'s own structural declaration (meta/save.ts) is
      // Wave I's actively in-progress territory this session, so this
      // mirrors `locked_seam`'s own toast-only, safely-repeatable pattern
      // instead of adding a new field there. Repeatable is safe by
      // construction: nothing here to farm.
      hud.showToast('🌉 THE VIEW — Corona Ave. stretches out past where the line ends.');
      break;
  }
}

// Camera shake: a peak strength + a fixed-duration decay, applied once per
// RENDERED frame (not per fixed update) in the loop's `render` callback —
// GameLoop can run 0+ fixed updates per frame, but render() runs exactly
// once, which is the only place a per-frame visual jitter belongs.
let shakeT = 0;
let shakeDuration = 1;
let shakeStrength = 0;
function triggerShake(strength: number, duration: number): void {
  if (!save.settings.shakeEnabled) return; // S07 accessibility toggle — every caller stays untouched, this is the one choke point
  // Only escalate — a small hit's shake shouldn't cut off a bigger one already playing (e.g. death).
  if (shakeT <= 0 || strength >= shakeStrength) {
    shakeStrength = strength;
    shakeDuration = duration;
    shakeT = duration;
  }
}

// A 0-height/width container (rare, but seen in some embedded webviews) must
// never produce a NaN aspect ratio — fall back to a sane default until a real
// resize event arrives.
const initialAspect = window.innerWidth && window.innerHeight ? window.innerWidth / window.innerHeight : 16 / 9;
const orbitCam = new OrbitCamera(initialAspect);
orbitCam.setOccluders(block.group.children);
orbitCam.camera.far = quality.cameraFar; // S08: device-tier draw distance, applied from frame 1 (not just a later settings-row change)
orbitCam.camera.updateProjectionMatrix();
// T3-4: sync the saved zoom preset BEFORE the file's first `snapTo()` call
// below — same "sync before the first snapTo()" discipline as S11's own
// insideBodega note and T3-1's block.setInteriorVisible() line right below
// this one. Placing this any later (e.g. alongside applyLeftyAndControlSize
// further down) would make frame 1 render at the default 'normal' distance
// regardless of what the player actually saved, since frame 1 runs zero
// update() ticks to correct it afterward.
orbitCam.setZoom(save.settings.cameraZoom);
// S11 fix: this is the file's OWN first-ever camera aim ("frame 1 has run
// zero update() ticks — aim now, not after several lerp frames"), but it was
// missing `activeCameraMaxDistance()` — every boot before S11 always started
// outdoors at this exact point (insideBodega could only ever become true
// LATER, via a live door interaction or the S07 resume block, both of which
// happen to run before this line but never made THIS specific call pass the
// interior clamp), so the gap was real but unobservable. S11's first-run
// flow (and the S07 resume-into-bodega path, whose own harness check has
// been separately compensating with an extra manual tick() ever since) can
// both now start with `insideBodega` already true at this exact line — a
// harness check against a genuinely fresh first-run boot caught the camera
// resolving at the outdoor pull-back distance instead of the interior one.
// T3-1: same "sync before the first snapTo()" discipline extends to the
// interior room's own visibility — `interiorGroup` defaults hidden
// (block.ts's own field comment), so a fresh first-run boot that starts
// `insideBodega===true` needs this explicit sync or the player would spawn
// inside an invisible room for one frame (and every frame after, until the
// next enter/exit toggled it).
block.setInteriorVisible(insideBodega);
// B-2: same "sync before the first snapTo()" discipline, for both new rooms
// — only reachable via the S07 resume path today (no first-run flow ever
// starts inside either one), but a resumed `insideLaundromat`/
// `insideBasement===true` needs the matching group visible from frame 1 too.
block.setLaundromatVisible(insideLaundromat);
block.setBasementVisible(insideBasement);
// Wave D: same "sync before the first snapTo()" discipline — only reachable
// via the S07 resume path (no first-run flow starts inside either den).
// `onEnter()` re-arms the encounter the same way walking in would, since
// `DenArena.state` itself is NOT persisted across a reload (matching
// `BossArena`'s own existing precedent for Gridlock).
block.setLairVisible(insideLair);
if (insideLair) lairEncounter.onEnter();
block.setSewerVisible(insideSewer);
if (insideSewer) sewerEncounter.onEnter();
orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance());
const input = new InputSystem(appEl);
input.onSlide = () => player.requestSlide();
const gamepadInput = new GamepadInput();

// S08: live quality-tier switching. `renderer.setPixelRatio`/the camera's
// far plane are both safe to change post-construction; `antialias` is a
// WebGL CONTEXT param fixed at renderer creation above and can't change
// without recreating the GL context, so it deliberately stays whatever the
// initial device-detected tier picked. Exposed on __BR3D__ for dev-mode
// testing/harness for now — S07's settings panel can call this from a
// quality-tier row whenever Karan wants that added.
function setQualityTier(tier: QualityTier): void {
  quality = getQualitySettings(tier);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.maxPixelRatio));
  orbitCam.camera.far = quality.cameraFar;
  orbitCam.camera.updateProjectionMatrix();
}

// --- S07: pause + settings panel ---
let uiState: UiState = 'playing';

function applyLeftyAndControlSize(): void {
  input.setLefty(save.settings.lefty);
  document.body.classList.toggle('lefty', save.settings.lefty);
  document.documentElement.style.setProperty('--br3d-ctl-scale', String(save.settings.controlSize));
}
applyLeftyAndControlSize();
// S08 spec-fidelity review finding (HIGH): the enemy-side half of the
// reduced-motion/flash toggle (ai/enemy.ts's flashEnabled param, threaded
// through EnemyManager.setFlashEnabled — built deliberately ahead of this
// wiring, since main.ts couldn't safely be touched again until S07's save
// system landed) was never actually connected to the live setting —
// EnemyManager.flashEnabled stayed hardcoded true forever, so toggling
// "HIT FLASH" off silently did nothing for enemies, only the player's own
// flash (which already reads save.settings.flashEnabled directly every
// tick). Synced once here at boot, same pattern as applyLeftyAndControlSize
// above; the ongoing sync on a live settings change is in
// applySettingsPatch below.
enemyManager.setFlashEnabled(save.settings.flashEnabled);
// T3-4's own boot-time zoom sync lives earlier in this file, right after
// `orbitCam` is constructed — BEFORE its first `snapTo()` call, not here
// (see that call site's own comment for why the ordering matters).

function applySettingsPatch(patch: Partial<Settings>): void {
  Object.assign(save.settings, patch);
  writeSave(save);
  if (patch.musicVolume !== undefined) audioBuses.setMusicVolume(patch.musicVolume);
  if (patch.sfxVolume !== undefined) audioBuses.setSfxVolume(patch.sfxVolume);
  if (patch.lefty !== undefined || patch.controlSize !== undefined) applyLeftyAndControlSize();
  if (patch.flashEnabled !== undefined) enemyManager.setFlashEnabled(patch.flashEnabled);
  if (patch.cameraZoom !== undefined) orbitCam.setZoom(patch.cameraZoom); // T3-4
}

// `pauseMenu`/`setUiState` reference each other via closures, not textual
// order — `function` declarations are hoisted, and neither is actually
// CALLED until a real button press or the Escape listener below, both of
// which only fire after this whole module has finished initializing.
function setUiState(next: UiState): void {
  if (uiState === next) return;
  if (uiState === 'playing' && next !== 'playing') {
    playSfx(audioBuses, 'pause'); // play BEFORE suspending — a cue scheduled into an already-suspended context wouldn't audibly land
    shakeT = 0; // a shake mid-flight when pause opens must not keep re-jittering the frozen render() every frame (shakeT only decays inside the now-gated tick())
    input.forceReset(); // a stale drag/stick captured at the moment pause opened must not survive underneath the overlay
  }
  uiState = next;
  pauseMenu.setVisible(uiState);
  huntBoard.setVisible(uiState === 'huntboard'); // S11 — reached directly from 'playing' via the hunt-board interactable, not nested inside PauseMenu's own two screens (see ui/pause.ts's UiState comment)
  wardrobeScreen.setVisible(uiState === 'wardrobe'); // Wave L-2 — same shape as huntBoard above
  stashScreen.setVisible(uiState === 'stash'); // Wave I — same shape as huntBoard/wardrobeScreen above
  if (uiState === 'playing') audioBuses.resumeFromPause();
  else audioBuses.suspendForPause();
}

const pauseMenu = new PauseMenu({
  getSettings: () => save.settings,
  onSettingsChange: applySettingsPatch,
  onStateChange: setUiState,
});

// S11: the bodega hunt board (ui/huntboard.ts). `onAccept`/`onAbandon` both
// return straight to 'playing' — there's no intermediate confirmation step,
// matching this game's existing "a tap on a menu option commits immediately"
// idiom (PauseMenu's own toggles/sliders apply live, no separate SAVE
// button).
const huntBoard = new HuntBoard({
  getActiveHunt: () => save.progress.activeHunt,
  // K-2 LEADS section — see meta/hunts.ts's own DEN_LEADS comment for why
  // both leads gate on the same 'utility_tunnel' checkpoint.
  isCheckpointDiscovered: (id) => checkpoints.hasDiscovered(id),
  isDenCleared: (leadId) => (leadId === 'rat_king_lair' ? save.progress.ratKingLairCleared : save.progress.sewerDenCleared),
  onAccept: (option) => {
    save.progress.activeHunt = { kind: option.kind, targetCount: option.targetCount, killedCount: 0, rewardCoins: option.rewardCoins, rewardScrap: option.rewardScrap };
    writeSave(save);
    hud.showToast(`🎯 HUNT ACCEPTED — ${huntLabelFor(option.kind)} x${option.targetCount}`);
    setUiState('playing');
  },
  onAbandon: () => {
    save.progress.activeHunt = null;
    writeSave(save);
    hud.showToast('Hunt abandoned.');
    setUiState('playing');
  },
  onClose: () => setUiState('playing'),
});

// Wave L-2 (PLAN-CITY2 §6): the bodega wardrobe stand (ui/wardrobe.ts) —
// same idiom as huntBoard above, one screen, driven by the same `uiState`.
const wardrobeScreen = new WardrobeScreen({
  isOwned: (id) => !!save.wardrobe.owned[id],
  getEquipped: (slot) => save.wardrobe.equipped[slot],
  onEquip: (slot, id) => {
    save.wardrobe.equipped[slot] = id;
    playerVisual.equip(slot, id);
    writeSave(save);
  },
  onClose: () => setUiState('playing'),
});

/**
 * Wave I (PLAN-CITY3 §5, I-3): THE STASH — same idiom as huntBoard/
 * wardrobeScreen above, one screen, driven by the same `uiState`.
 * `getRecipeHints` composes its text from the SAME constants the real craft
 * interaction (handleInteract's 'craft' case) reads, so a future rebalance
 * can't silently desync the hint from the real cost.
 */
function getRecipeHints(): { scrapHint: string; partHint: string } {
  if (!save.progress.armorCrafted) {
    return {
      scrapHint: `→ Scrap Armor (${wallet.materials.scrap}/${ARMOR_COST_SCRAP})`,
      partHint: 'Riot Plating (craft Scrap Armor first)',
    };
  }
  if (!save.progress.platingCrafted) {
    return {
      scrapHint: 'Salvage Exchange (unlocks after Riot Plating)',
      partHint: `→ Riot Plating (${wallet.materials.part}/${RIOT_PLATING_COST_PARTS})`,
    };
  }
  // Review fix (MEDIUM, spec-fidelity): the ORIGINAL scrapHint here claimed
  // scrap feeds Salvage Exchange — it doesn't (economy/craft.ts's own
  // attemptNextCraftTier spends ONLY `part` for this tier; scrap is a pure
  // OUTPUT). Once both prior tiers are crafted, scrap genuinely has no
  // further recipe — say so honestly instead of duplicating partHint's text
  // under the wrong material.
  return {
    scrapHint: 'No further recipe — well spent.',
    partHint: `→ Salvage Exchange (${wallet.materials.part}/${SALVAGE_EXCHANGE_COST_PARTS})`,
  };
}
const stashScreen = new StashScreen({
  getWallet: () => ({ coins: wallet.coins, scrap: wallet.materials.scrap, part: wallet.materials.part }),
  getRecipeHints,
  getTasks: () => buildTasks(save.progress, save.wardrobe),
  getSessionLog,
  getLifetimeStats: () => save.progress.stats,
  isOwned: (id) => !!save.wardrobe.owned[id],
  getEquipped: (slot) => save.wardrobe.equipped[slot],
  onEquip: (slot, id) => {
    save.wardrobe.equipped[slot] = id;
    playerVisual.equip(slot, id);
    writeSave(save);
  },
  onClose: () => setUiState('playing'),
});

// Desktop/harness convenience — not the only way to pause (the real control
// is PauseMenu's own on-screen button). Gated the same single-path way as
// every button (`setUiState`), so there's no divergent toggle for pause to
// stack behind (Classic gotcha #39's exact lesson): SETTINGS ignores
// Escape entirely, same as it ignores every button except its own BACK.
window.addEventListener('keydown', (e) => {
  if (e.code !== 'Escape') return;
  if (uiState === 'playing') setUiState('paused');
  else if (uiState === 'paused') setUiState('playing');
});

let overlay: DevOverlay | null = null;
if (devMode) {
  overlay = new DevOverlay({
    renderer,
    getEntityCount: () => 1 + enemyManager.getEnemies().length + projectiles.activeCount(),
    getAimPreset: () => aimPreset,
    onAimPresetChange: (preset) => {
      setAimPresetAndPersist(preset);
    },
  });
}

function resize(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (w === 0 || h === 0) return; // a hidden/0x0 viewport must never drive resize math
  renderer.setSize(w, h);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.maxPixelRatio)); // devicePixelRatio can change (monitor swap, browser zoom)
  orbitCam.setAspect(w / h);
}
window.addEventListener('resize', resize);
resize();

// Dev-only teleport: tap (not drag) the right half to raycast onto the ground
// and jump the player there. Left half is the stick's zone (input.ts uses
// `>=` at the same boundary, so exactly one of the two ever claims a
// boundary-column tap). Gated on drag distance, not just pointerdown, so a
// real camera-orbit drag on this same half never also fires a teleport.
if (devMode) {
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const TAP_MAX_DRAG_PX = 10;
  let downX = 0;
  let downY = 0;
  let tracking = false;

  canvas.addEventListener('pointerdown', (e) => {
    if (e.clientX < window.innerWidth / 2) return;
    tracking = true;
    downX = e.clientX;
    downY = e.clientY;
  });
  canvas.addEventListener('pointerup', (e) => {
    if (!tracking) return;
    tracking = false;
    if (Math.hypot(e.clientX - downX, e.clientY - downY) > TAP_MAX_DRAG_PX) return; // was a drag, not a tap
    pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
    pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
    raycaster.setFromCamera(pointer, orbitCam.camera);
    const hit = block.raycastGround(raycaster);
    if (hit) {
      player.teleport(hit.x, hit.z);
      orbitCam.snapTo(player.mesh.position); // cut, don't slide the camera across the map
    }
  });
}

function respawnPlayer(): void {
  playerDead = false;
  playerHealth.hp = playerHealth.maxHp;
  playerHealth.invulnerableT = RESPAWN_INVULN_MS / 1000;
  playerHealth.knockbackVel.set(0, 0, 0);
  // S05: respawns at the currently-active CHECKPOINT (world/checkpoints.ts)
  // instead of S02's hardcoded always-bodega-door offset. Defaults to
  // `spawn` until the player reaches a real checkpoint trigger — see
  // checkpoints.ts's own header comment for why `spawn`, not `bodega`, is
  // the deliberate default.
  const cp = checkpoints.current;
  player.teleport(cp.respawnX, cp.respawnZ);
  orbitCam.snapTo(player.mesh.position, activeCameraMaxDistance());
  hud.hideBanner();
}

// --- S07: periodic suspend snapshot + save-on-exit ---
const SNAPSHOT_INTERVAL_SECONDS = 4;
let snapshotTimer = 0;

/**
 * Writes `save.suspended` (meta/save.ts) — called periodically from tick()
 * and on tab-hide/unload below. Never captured while `playerDead` — a
 * snapshot taken mid-death/respawn would resume the player dead forever
 * with no death UI showing, worse than just keeping the last living
 * snapshot. This is the ONLY place `save.suspended` is written.
 */
function snapshotSuspendedRun(): void {
  if (playerDead) return;
  save.suspended = {
    playerX: player.position.x,
    playerZ: player.position.z,
    checkpointId: checkpoints.current.id,
    hp: playerHealth.hp,
    coins: wallet.coins,
    scrap: wallet.materials.scrap,
    part: wallet.materials.part, // Wave L-1/L-3 review fix (HIGH) — was missing entirely, silently losing every accumulated part on reload
    insideBodega, // review fix (HIGH) — see the resume block's own comment for why this is required, not optional
    insideLaundromat, // B-2: same requirement, same reasoning, one flag per room
    insideBasement,
    insideLair, // Wave D: same requirement, same reasoning, two more rooms
    insideSewer,
  };
  writeSave(save);
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) snapshotSuspendedRun();
});
window.addEventListener('beforeunload', snapshotSuspendedRun);

/**
 * S11: the hunt-board kill-tracking call site — called once per confirmed
 * kill from every `DamageEvent[]` loop in tick() (bullets, melee, the super
 * blast), reading each event's own `kind` (combat/resolve.ts's S11
 * addition). A no-op whenever there's no active hunt, or the killed kind
 * doesn't match it — cheap enough to call unconditionally on every kill
 * rather than gate the call site itself. On completion, grants the reward
 * BAKED ONTO the hunt at accept-time (never re-derived from the board's
 * templates, which may have since changed) and persists immediately —
 * bigger and more surprising to lose than an ordinary kill's coins, same
 * "durable, don't wait for the periodic snapshot" call as `hidden_stash`/the
 * craft recipe above. A completed hunt does NOT auto-offer a new one — the
 * player returns to the board on their own, by design (the "return" half of
 * §6.2's "bodega-hunt-return-craft loop").
 */
function registerHuntKill(kind: EnemyKind | undefined): void {
  if (!kind) return;
  const hunt: ActiveHunt | null = save.progress.activeHunt;
  if (!hunt || hunt.kind !== kind) return;
  hunt.killedCount++;
  if (hunt.killedCount >= hunt.targetCount) {
    grantCoins(wallet, hunt.rewardCoins);
    grantMaterial(wallet, 'scrap', hunt.rewardScrap);
    save.progress.huntsCompleted++;
    save.progress.activeHunt = null;
    snapshotSuspendedRun();
    // I-4 (PLAN-CITY3 §5): "hunt-complete toast names the next step" — one
    // combined toast, not a second one (Hud.showToast's own "latest wins
    // immediately" semantics would just clobber a follow-up call). The next
    // task is recomputed AFTER the two writes above, so a hunts-completed
    // milestone or a wardrobe change this exact hunt just triggered is
    // already reflected.
    const next = nextUndoneTask(buildTasks(save.progress, save.wardrobe));
    const nextText = next ? `Next: ${next.label}` : 'All objectives complete!';
    hud.showToast(`✅ HUNT COMPLETE — +${hunt.rewardCoins} coins, +${hunt.rewardScrap} scrap · ${nextText}`);
    playSfx(audioBuses, 'huntComplete'); // Wave I (I-1): a dedicated cue, was the same flat 'pickup' blip
  }
}

/**
 * Review fix (HIGH, spec-fidelity): the durable half of I-3's own
 * `Progress.stats.killsByKind` line — called alongside every existing
 * `registerHuntKill`/boss-kill call site (roster enemies via `EnemyKind`,
 * bosses via their own `BossLootKey`-shaped string id, e.g. `'gridlock'`),
 * same "arbitrary string key, no fixed vocabulary" shape `everKilled` and
 * `placedCollectibles` already use.
 */
function registerKillStat(kind: string): void {
  const kbk = save.progress.stats.killsByKind;
  kbk[kind] = (kbk[kind] ?? 0) + 1;
}

/**
 * S06: the boss-kill choke point's call site — invoked from EVERY damage-in
 * path that can land the lethal hit (bullets, melee, super), each checking
 * its own `DamageEvent.killed` flag rather than the FSM trying to detect the
 * transition later (see the long comment inside tick()'s boss block for why
 * that approach doesn't work — `takeDamage()` runs between update() calls,
 * so a same-function "was I dead a moment ago" check can never observe it).
 * `Boss.takeDamage()`'s own `if (this.state === 'dead') return false;` guard
 * means `killed` can only ever be true ONCE across all three paths even if
 * two land in the same tick, so this is safe to call from multiple sites.
 */
function handleBossDefeated(): void {
  // Wave L-1: the only boss reachable live today is Gridlock — `boss!` is
  // safe here (non-null assertion) for the same reason `aimTargets.push(
  // boss!.aimTarget)` above already asserts it: this function is only ever
  // called from a `killed:true` event derived from `engagedBoss()`, which
  // itself requires `boss` to be non-null.
  resolveBossKill('gridlock', boss!.position.clone(), pickups, buildLootContext(), superMeter);
  markKilledOnce('gridlock');
  registerKillStat('gridlock'); // Wave I review fix: Progress.stats.killsByKind
  bossArena.clear();
  hud.hideBossBar();
  // Wave D-4: the one-time-forever first-clear bonus (flat grant, per-kill
  // loot above is unaffected/unchanged) — matches the same shape as the two
  // den bonuses below (world/dens.ts's LairEncounter/SewerDenEncounter own
  // `justDefeated` edge, resolved in tick()).
  // K-2 fix (same class the plan calls out for the Rat King specifically,
  // confirmed present here too): `Hud.showToast`'s own contract is "a
  // second call while one is already showing CLOBBERS it" — the old code
  // fired 'DEFEATED' then, on the SAME synchronous call, a first-clear
  // toast that destroyed it before it ever rendered a frame. One combined
  // toast (the box wraps naturally at `.br3d-toast`'s own max-width, no
  // explicit newline needed) instead of two racing calls.
  if (!save.progress.gridlockLairCleared) {
    save.progress.gridlockLairCleared = true;
    grantCoins(wallet, DEN_FIRST_CLEAR_GRIDLOCK.coins);
    grantMaterial(wallet, 'scrap', DEN_FIRST_CLEAR_GRIDLOCK.scrap);
    snapshotSuspendedRun();
    hud.showToast(`GRIDLOCK GOLEM DEFEATED — 🏆 FIRST CLEAR +${DEN_FIRST_CLEAR_GRIDLOCK.coins} COINS, +${DEN_FIRST_CLEAR_GRIDLOCK.scrap} SCRAP`);
  } else {
    hud.showToast('GRIDLOCK GOLEM DEFEATED');
  }
}

/** Wave I (I-1): the fly-arc's own launch point — a standard three.js NDC projection through the CURRENT camera, read fresh every call (never cached) since the camera moves every tick. */
function worldToScreen(pos: THREE.Vector3): { x: number; y: number } {
  const v = pos.clone().project(orbitCam.camera);
  return { x: (v.x * 0.5 + 0.5) * window.innerWidth, y: (1 - (v.y * 0.5 + 0.5)) * window.innerHeight };
}

function tick(dt: number): void {
  input.update();
  // S07: pause gate. Still drains every edge-triggered press below (same
  // discipline as the death-window fix, S02's own review) even though the
  // pause overlay already physically blocks new presses from reaching these
  // buttons, and setUiState's forceReset() already cleared any drag/stick
  // active the instant pause opened — draining defensively costs nothing
  // and closes the same latch-on-resume bug class by construction rather
  // than by relying on DOM covering alone.
  if (uiState !== 'playing') {
    input.consumeLook();
    input.consumeJump();
    hud.consumeSuperPress();
    hud.consumeAimCyclePress();
    hud.consumeWeaponCyclePress();
    hud.consumeInteractPress();
    // S08 spec-fidelity review finding (MEDIUM): every OTHER input source is
    // drained here specifically so a held press doesn't read as a fresh edge
    // the instant the game resumes (the same latch-through-pause bug class
    // this project has already found and fixed once, for jump/super across
    // the death/respawn window). gamepadInput.poll() must be drained the
    // same way, or its internal prevPressed state goes stale while paused
    // and a controller button held across the pause boundary fires
    // unrequested on the very first post-resume tick.
    gamepadInput.poll();
    return;
  }
  // S08 controller support: a standalone poller (core/gamepad.ts), merged in
  // HERE rather than reaching into InputSystem's own internals (S07 added a
  // `lefty` flag to that same class) — mutating `input.move` in place means
  // every existing downstream reader (orbitCam.followMovement, player.update)
  // picks up gamepad movement for free, with zero other call sites touched.
  // Only overrides when the stick is genuinely deflected, so an idle/
  // disconnected pad never fights real touch/keyboard input. Polled only on
  // the "still playing" path (below the pause-gate return above), matching
  // every other per-tick input read in this function.
  const gp = gamepadInput.poll();
  if (gp.connected && Math.hypot(gp.moveX, gp.moveY) > 0.01) {
    input.move.x = gp.moveX;
    input.move.y = gp.moveY;
  }
  const look = input.consumeLook();
  if (gp.connected) {
    look.dx += gp.lookDx;
    look.dy += gp.lookDy;
  }
  orbitCam.applyLook(look.dx, look.dy, LOOK_SENSITIVITY);
  // Refresh the camera-relative basis (and resolve any wall obstruction)
  // against the player's position as of the END of last tick — a one-tick
  // lag that's imperceptible at 60fps and avoids a circular dependency
  // (the controller needs this basis before it can move this tick).
  // maxDistance shrinks to the interior's own scale while inside the
  // bodega — see activeCameraMaxDistance()'s comment above.
  orbitCam.update(player.mesh.position, dt, activeCameraMaxDistance());

  // T1 auto-follow: hands the RAW stick input straight to followMovement,
  // which turns the camera at a bounded, theta-proportional rate (see that
  // method's comment for the invariance property that rules out any
  // ease-to-target design here — two earlier versions of this fix failed on
  // it). main.ts deliberately does NOT pre-project a world direction
  // through the camera's own rotating basis. Skipped entirely while dead
  // (no movement is being applied that tick either — see the `!playerDead`
  // gate below).
  if (!playerDead) {
    orbitCam.followMovement(input.move.x, input.move.y, dt);
  }

  mothSwarm.update(dt); // S10: pure ambience — always animates while playing, unrelated to hitstop/playerDead (matches how the camera/world otherwise never freeze on a kill)

  const hitstopActive = tickHitstop(dt); // world-sim (AI/projectiles) flinch from a kill armed on a PRIOR tick — player/camera keep moving regardless

  // Both edge-triggered presses are drained EVERY tick regardless of
  // `playerDead` — only whether they're ACTED ON is gated below. A press
  // that arrives while dead must not linger in a latch (Hud.superPressed/
  // InputSystem's own jump-button state) and then fire unrequested the
  // instant the player respawns (confirmed real bugs, S02's own review).
  const jumpPressed = input.consumeJump() || gp.jumpPressed;
  const superPressed = hud.consumeSuperPress() || gp.superPressed;
  const interactPressed = hud.consumeInteractPress() || gp.interactPressed;

  if (!playerDead && jumpPressed) {
    player.requestJump();
    playSfx(audioBuses, 'jump');
  }
  if (!playerDead && gp.slidePressed) player.requestSlide(); // S08: gamepad B button
  if (!playerDead) {
    player.update(dt, input.move, orbitCam.getForward(), orbitCam.getRight());
  }
  // S07: gated on the accessibility toggle — `0` reads as "no flash" the
  // same way a naturally-expired `flashT` already does, so this needs no
  // separate branch inside PlayerCapsule itself.
  player.applyHitFlash(save.settings.flashEnabled && playerHealth.flashT > 0 ? playerHealth.flashT / FLASH_DURATION : 0);

  // Wave L-2: the real player visual follows the capsule's own live
  // position/facing/state — see world/playerVisual.ts's header comment.
  // Gated the same `!playerDead` way `player.update()` itself is just above
  // (movement is frozen while dead anyway, so the visual freezing too is
  // consistent, not a new behavior). Hides the capsule's OWN mesh exactly
  // once, the first tick the GLB is actually ready — never earlier (nothing
  // would render in its place before then) and never repeatedly (a single
  // boolean latch, not a per-tick `.visible = false` write).
  if (!playerDead) {
    playerVisual.update(dt, player.position, player.getFacing(), {
      moving: Math.hypot(player.lastDesiredDirX, player.lastDesiredDirZ) > 0.01,
      grounded: player.state.grounded,
      vaulting: player.state.vaulting,
      sliding: player.state.sliding,
      speed: player.getCommandedSpeed(),
    });
    if (!playerVisualCapsuleHidden && playerVisual.ready) {
      player.mesh.visible = false;
      playerVisualCapsuleHidden = true;
    }
  }

  // S05: checkpoints/interact prompts — gated on `!playerDead` like every
  // other position-driven system in this file, and explicitly cleared in
  // the `else` branch so a prompt reached right before dying doesn't sit
  // frozen on screen through the death banner.
  if (!playerDead) {
    const checkpointHit = checkpoints.update(player.position.x, player.position.z, player.position.y);
    if (checkpointHit) {
      hud.showToast(`CHECKPOINT — ${checkpointHit.label}`);
      playSfx(audioBuses, 'checkpoint');
    }

    const nearInteractable = interact.update(player.position.x, player.position.z, player.position.y);
    hud.setInteractPrompt(nearInteractable ? nearInteractable.label : null);
    // The press was already drained above regardless (S02's always-drain
    // law) — the grace timer only stops it from ACTING right after a
    // walk-out auto-exit, so an in-flight EXIT tap can't re-enter (see
    // INTERACT_GRACE_AFTER_AUTO_TRANSITION's own comment).
    if (interactGraceTimer > 0) interactGraceTimer = Math.max(0, interactGraceTimer - dt);
    if (interactPressed && nearInteractable && interactGraceTimer <= 0) {
      handleInteract(nearInteractable);
      playSfx(audioBuses, 'interact');
    }
  } else {
    hud.setInteractPrompt(null);
  }

  if (hud.consumeAimCyclePress() || gp.aimCyclePressed) {
    setAimPresetAndPersist(AIM_PRESETS[(AIM_PRESETS.indexOf(aimPreset) + 1) % AIM_PRESETS.length]);
  }
  if (hud.consumeWeaponCyclePress() || gp.weaponCyclePressed) {
    setWeaponAndPersist(WEAPON_ORDER[(WEAPON_ORDER.indexOf(weapon.currentId) + 1) % WEAPON_ORDER.length]);
  }

  if (!playerDead) {
    tickHealth(playerHealth, dt);
    player.applyExternalDelta(playerHealth.knockbackVel.x * dt, playerHealth.knockbackVel.z * dt);

    if (!hitstopActive) {
      // Wave D-1: `camera`/`spottedElite` are retired from this call — the
      // roaming Rat King (and its own frustum-gated respawn/"spotted" banner)
      // no longer exists; ai/spawner.ts's own Wave D comment has the story.
      const spawnTick = enemyManager.update(dt, block.physics, player.position, navGrid);

      // S11: the golden_pigeon rare-roamer stub's own announcement — fires
      // only on a genuine post-boot RESPAWN (ai/spawner.ts's own comment on
      // why the initial world population never triggers this), so it always
      // means "one has just appeared," never "one has always been here."
      if (spawnTick.goldenPigeonSpotted) {
        hud.showToast('✨ A GOLDEN PIGEON has appeared — find it!');
      }

      if (resolveMeleeAttacksOnPlayer(spawnTick.meleeAttackers, player.position, playerHealth, player.state.invulnerable)) {
        triggerShake(0.12, 0.12);
        playSfx(audioBuses, 'hurt');
      }

      for (const req of spawnTick.projectilesToFire) {
        const archetype = req.enemy.archetype;
        const { velocity } = computeLobVelocity(req.origin, req.targetPos, archetype.projectileSpeed);
        projectiles.fire({
          owner: 'enemy',
          origin: new THREE.Vector3(req.origin.x, req.origin.y + 0.9, req.origin.z),
          velocity,
          damage: archetype.projectileDamage,
          knockbackForce: 3.5,
          radius: 0.2,
          life: 3,
          gravity: LOB_GRAVITY,
          color: 0x8a6a3a,
        });
        playSfx(audioBuses, 'enemyAttack', req.origin, player.position);
      }

      // `spawnTick.removed` fires when an enemy's DEATH-FADE ANIMATION
      // finishes (~0.5s after the actual killing blow, see Enemy.update's
      // `dead` branch/DEATH_FADE_DURATION) — NOT at the moment it died.
      // Hitstop is already armed at the real moment of death by both kill
      // paths above it (resolveProjectileHitsOnEnemies's hitEvents below,
      // and applySuperBlast) — arming it again here would be a second,
      // mistimed flinch firing half a second late, unrelated to anything
      // the player is doing at that moment. No loot/progression in S02
      // (§0.8) either, so this loop is currently just a hook for a future
      // phase (score/loot on despawn), not a place to trigger effects.
      void spawnTick.removed;

      // S06: boss framework. Gated inside the SAME `!playerDead`/
      // `!hitstopActive` scope as enemyManager.update() above — a boss
      // fight freezes on hitstop and stops advancing while the player is
      // down, exactly like every other world-sim system in this file.
      if (boss) {
        bossArena.tickClearedTimer(dt); // T3-5: the interim 5-minute lair-respawn timer — a no-op outside 'cleared'
        if (bossArena.state === 'dormant' && bossArena.isInsideEntryRadius(player.position.x, player.position.z)) {
          bossArena.activate();
          boss.engage();
          boss.group.visible = true; // K-1(d): the crossing beat itself — this IS the "walked threshold" moment (isInsideEntryRadius is now the physical pocket rectangle, K-1(c))
          hud.showBossBar();
          hud.showToast('GRIDLOCK GOLEM'); // a self-clearing notification, not `banner` (reserved for the death screen — see Hud's own header comment on why the two must never race)
        }
        // K-1(a): on-deck is a continuous fact about the player's CURRENT
        // position, read fresh every tick and consumed by Boss ONLY at the
        // exact shockwave impact tick (ai/boss.ts's own `updateAttack1`
        // comment) — computing it here, not inside Boss, keeps Boss
        // decoupled from wreck geometry per its own established convention.
        const playerOnWreckDeck = isOnGridlockWreckDeck(player.position.x, player.position.y, player.position.z);
        // Review fix (HIGH, confirmed by all 3 lenses): this MUST run BEFORE
        // boss.update() below, not after — resolveProjectileHitsOnBreakableProjectiles
        // (further down in tick()) deactivates the hubcap projectile the SAME
        // tick it's shot down, so activeHubcap.active already reads false by
        // the very next tick's hubcapInFlight computation. If reportHubcapBroken()
        // ran AFTER boss.update() (the original order), updateAttack2Wait would
        // already have committed to the "unbroken" exit using a still-false
        // currentAttackBroken one statement before this call could set it —
        // the hubcap-break path was structurally unreachable in real play.
        if (hubcapBrokenThisResolve) {
          boss.reportHubcapBroken();
          hubcapBrokenThisResolve = false;
        }
        const hubcapInFlight = activeHubcap !== null && activeHubcap.active;
        const bossResult = boss.update(dt, player.position, bossArena.state === 'active', block.physics, playerOnWreckDeck, player.state.invulnerable, hubcapInFlight);
        if (!hubcapInFlight) activeHubcap = null; // resolved (hit, expired, or broken) — stop tracking it
        if (bossArena.state === 'active') {
          if (bossResult.shockwaveFired) {
            // K-1(a): "broken" (on-deck OR sliding through the impact frame)
            // means the shockwave deals NO damage at all, not just "no
            // exposed reward" — reuses the existing `slideInvulnerable`
            // no-damage gate `resolveBossShockwaveOnPlayer` already has,
            // simply ORed with the on-deck fact computed above.
            if (resolveBossShockwaveOnPlayer(boss.position, bossResult.shockwaveRadius, bossResult.shockwaveDamage, player.position, playerHealth, playerOnWreckDeck || player.state.invulnerable)) {
              triggerShake(boss.isEnraging ? 0.32 : 0.22, boss.isEnraging ? 0.3 : 0.2);
              playSfx(audioBuses, 'hurt'); // review finding: every other "player got hit" path in this file (melee/projectile) pairs its shake with this cue — the shockwave's own bespoke AoE call site was the one gap
            }
            if (boss.isEnraging) hud.showToast('⚠️ GRIDLOCK GOLEM — ENRAGED'); // K-1(b): the HUD cue half of the enrage's own tell (the body tint is the other half, ai/boss.ts's applyMechanicTint)
          }
          if (bossResult.projectileToFire) {
            // Review finding: computeLobVelocity's vy solve and the actual
            // fire() origin used to disagree (0.9m assumed vs. 1.6m real),
            // silently arriving ~0.7m higher than intended — masked only by
            // VERTICAL_ENGAGEMENT_RANGE's 2.5m tolerance. HUBCAP_LAUNCH_HEIGHT
            // is now the ONE number both calls share (boss.ts), so they can't
            // drift apart again (gotcha #19).
            const hubcapOrigin = new THREE.Vector3(
              bossResult.projectileToFire.origin.x,
              bossResult.projectileToFire.origin.y + HUBCAP_LAUNCH_HEIGHT,
              bossResult.projectileToFire.origin.z,
            );
            const { velocity } = computeLobVelocity(hubcapOrigin, bossResult.projectileToFire.targetPos, HUBCAP_SPEED);
            // K-1(a): breakable + a small HP pool — see
            // `resolveProjectileHitsOnBreakableProjectiles`'s own call site
            // further down for where player fire actually depletes this.
            activeHubcap = projectiles.fire({
              owner: 'enemy',
              origin: hubcapOrigin,
              velocity,
              damage: HUBCAP_DAMAGE,
              knockbackForce: 4.5,
              radius: HUBCAP_RADIUS,
              life: 3,
              gravity: LOB_GRAVITY,
              color: HUBCAP_COLOR,
              breakable: true,
              hp: HUBCAP_BREAK_HP,
            });
            playSfx(audioBuses, 'enemyAttack', hubcapOrigin, player.position); // review finding: the trash-can lobber's own fire loop plays this cue right after its own projectiles.fire() — the hubcap's structurally identical launch was missing it (its landing-on-player 'hurt' cue is already free via the shared resolveProjectileDamageOnPlayer path)
          }
          // A kill itself is handled at the exact damage-resolution call site
          // (handleBossDefeated(), called from the bullets/melee/super hit
          // checks further down) — NOT detected here via a "did state become
          // dead" poll. `takeDamage()` is called from OUTSIDE this update()
          // call (main.ts's own damage-resolution code runs BETWEEN two
          // update() ticks), so a wasAlive-style snapshot taken at the top of
          // THIS call can never observe the transition — it would always
          // read one tick late. `boss.state !== 'dead'` below just guards
          // against a stray reset/HP-bar-update during the harmless one-tick
          // lag between a same-tick kill (resolved further down in tick(),
          // after this block already ran) and handleBossDefeated()'s own
          // `bossArena.clear()` taking effect on the NEXT tick.
          if (boss.state !== 'dead' && bossArena.isOutsideResetRadius(player.position.x, player.position.z)) {
            // §5's "arena gating": retreating far enough mid-fight resets the
            // encounter rather than letting it be worn down via hit-and-run
            // kiting across the whole block (world/arena.ts's own header
            // comment has the full reasoning).
            boss.resetEncounter();
            bossArena.reset();
            boss.group.visible = false; // review fix (LOW): re-arm K-1(d)'s hide-until-threshold-crossed reveal for the next approach — was left visible forever after the first encounter
            hud.hideBossBar();
          } else if (boss.state !== 'dead') {
            hud.updateBossBar('GRIDLOCK GOLEM', boss.health.hp, boss.health.maxHp, boss.isExposed);
          }
        } else if (bossArena.state === 'cleared' && bossArena.isOutsideResetRadius(player.position.x, player.position.z) && bossArena.clearedTimerDone) {
          // S10 review finding (HIGH — the arena had no path back to
          // 'dormant' after a real kill, so PLAN-CITY §5's own "repeatable
          // Gridlock lair" line was false: the boss could be fought at most
          // once per page load). `handleBossDefeated()`'s `bossArena.
          // clear()` is meant to be a temporary "just won, don't instantly
          // re-engage while the kill banner is still showing" state, not a
          // permanent one — nothing ever bridged it back. Reuses the SAME
          // retreat-far-enough threshold the mid-fight reset above already
          // uses (`isOutsideResetRadius`) rather than inventing a second
          // one: once the player has genuinely left the lair, resurrect the
          // boss (full HP, home position, scale — `resetEncounter()` is
          // this codebase's own designed general-purpose re-fight
          // mechanism per its header comment) and reopen the arena so the
          // NEXT approach re-triggers a fresh encounter exactly like the
          // first one.
          //
          // T3-5 addition: ALSO requires `clearedTimerDone` — an interim
          // 5-minute lair-respawn timer (world/arena.ts's own
          // `LAIR_RESPAWN_DELAY`) on top of the pre-existing retreat
          // requirement, until Wave D's real den rules (PLAN-CITY2 §4)
          // replace this whole branch with something den-specific.
          boss.resetEncounter();
          bossArena.reset();
          boss.group.visible = false; // review fix (LOW): same re-arm as the mid-fight retreat path above — a resurrected boss should also hide again until the next threshold crossing
        }
      }

      // Wave D-4 fix: tick both dens' post-clear respawn clocks EVERY frame,
      // unconditionally — same contract as Gridlock's own `bossArena.
      // tickClearedTimer(dt)` two blocks above (real elapsed time,
      // decoupled from the player's location; resurrection itself still
      // requires being outside the room at the moment it fires). See
      // `LairEncounter.tickRespawnClock`'s own comment for the bug this
      // closes: neither den had ANY path back to 'dormant' after a kill
      // before this call existed.
      if (lairEncounter.boss) lairEncounter.tickRespawnClock(dt, insideLair);
      if (sewerEncounter.elite) sewerEncounter.tickRespawnClock(dt, insideSewer);

      // Wave D-1: the Rat King lair — mirrors Gridlock's own block above in
      // shape (engage on entry, resolve the tick result against the player,
      // HUD bar, first-clear bonus, leave+timer respawn) but keyed on the
      // room's own `insideLair` flag instead of a radius (there is no open-
      // pavement approach to retreat across — the den is a real enclosed
      // room, entered/exited only via the tunnel-breach teleport door).
      if (lairEncounter.boss && insideLair) {
        const ratResult = lairEncounter.tick(dt, player.position, block.physics);
        const ratKing = lairEncounter.boss as RatKingBoss;
        if (lairEncounter.arena.state === 'active') {
          if (ratResult?.meleeFired) {
            if (resolveRatKingMeleeOnPlayer(ratKing.position, player.position, playerHealth, player.state.invulnerable)) {
              triggerShake(0.18, 0.18);
              playSfx(audioBuses, 'hurt');
            }
          }
          if (ratResult?.chargeHitPlayer) {
            if (resolveRatKingChargeOnPlayer(ratKing.position, player.position, playerHealth, player.state.invulnerable)) {
              triggerShake(0.28, 0.22);
              playSfx(audioBuses, 'hurt');
            }
          }
          if (ratKing.state !== 'dead') {
            hud.updateBossBar('THE RAT KING', ratKing.health.hp, ratKing.health.maxHp, ratKing.isExposed);
          }
        }
        if (lairEncounter.justDefeated) {
          hud.hideBossBar();
          resolveBossKill('rat_king', ratKing.position.clone(), pickups, buildLootContext(), superMeter);
          markKilledOnce('rat_king');
          registerKillStat('rat_king'); // Wave I review fix: Progress.stats.killsByKind
          // K-2: "boss-defeat + first-clear toasts merged into one two-line
          // toast (today the second destroys the first in the same frame)"
          // — Hud.showToast's own contract is a second call clobbers the
          // first; one combined string instead of two racing calls.
          if (!save.progress.ratKingLairCleared) {
            save.progress.ratKingLairCleared = true;
            grantCoins(wallet, DEN_FIRST_CLEAR_RATKING.coins);
            grantMaterial(wallet, 'scrap', DEN_FIRST_CLEAR_RATKING.scrap);
            snapshotSuspendedRun();
            hud.showToast(`THE RAT KING DEFEATED — 🏆 FIRST CLEAR +${DEN_FIRST_CLEAR_RATKING.coins} COINS, +${DEN_FIRST_CLEAR_RATKING.scrap} SCRAP`);
          } else {
            hud.showToast('THE RAT KING DEFEATED');
          }
        }
        if (lairEncounter.arena.state === 'active') hud.showBossBar();
      }
      // K-2: the arrival toast now genuinely fires — see `enterLair()`'s own
      // comment (fixing the stale claim this comment used to make).

      // Wave D-3: the sewer den elite — a single `Enemy` (burrower), not a
      // Boss-shaped FSM, so its own contact hit resolves through the SAME
      // `meleeAttackFired`-style tick result every roster enemy uses
      // (ai/archetypes.ts's own `sewer_elite.contactDamage` comment).
      if (sewerEncounter.elite && insideSewer) {
        const eliteResult = sewerEncounter.tick(dt, player.position, block.physics);
        if (sewerEncounter.arena.state === 'active' && eliteResult?.meleeAttackFired) {
          if (resolveMeleeAttacksOnPlayer([sewerEncounter.elite], player.position, playerHealth, player.state.invulnerable)) {
            triggerShake(0.16, 0.16);
            playSfx(audioBuses, 'hurt');
          }
        }
        if (sewerEncounter.justDefeated) {
          // K-2: same toast-clobber fix as Gridlock/the Rat King above.
          if (!save.progress.sewerDenCleared) {
            save.progress.sewerDenCleared = true;
            grantCoins(wallet, DEN_FIRST_CLEAR_SEWER.coins);
            grantMaterial(wallet, 'scrap', DEN_FIRST_CLEAR_SEWER.scrap);
            snapshotSuspendedRun();
            hud.showToast(`SEWER ELITE DEFEATED — 🏆 FIRST CLEAR +${DEN_FIRST_CLEAR_SEWER.coins} COINS, +${DEN_FIRST_CLEAR_SEWER.scrap} SCRAP`);
          } else {
            hud.showToast('SEWER ELITE DEFEATED');
          }
        }
      }
    }

    weapon.update(dt);
    const aimTargets = [...enemyManager.getEnemies(), ...denExtraEnemies()].map((e) => e.aimTarget);
    if (engagedBoss()) aimTargets.push(boss!.aimTarget);
    // Wave D-1: RatKingBoss's own aimTarget, same "engaged only" gate.
    if (lairEncounter.arena.state === 'active' && lairEncounter.boss) aimTargets.push((lairEncounter.boss as RatKingBoss).aimTarget);
    const aim = resolveAimDirection(aimPreset, player.position, player.getFacing(), aimTargets);
    // T2 hold-fire (Karan's second playtest: "turn the gun off unless there
    // is an enemy nearby — once the enemy is dead it doesn't make sense to
    // keep shooting"): assisted presets fire only while an enemy is
    // genuinely in range; FREE AIM ('forward') is the deliberate
    // always-fire option he asked to keep. The cooldown keeps ticking down
    // in weapon.update() regardless, so fire resumes instantly (no stale
    // cooldown) the moment an enemy re-enters range.
    //
    // S04 review finding: AIM_RANGE (18) is one shared "is anything worth
    // engaging nearby" gate across every weapon, not a per-weapon reach
    // guarantee — Stickball Bat's real range is 2m, Sawed-Off's is 7m.
    // Without this second gate, an assisted preset would fire/swing at the
    // resolved target on every cooldown tick even when it's well outside
    // the EQUIPPED weapon's own reach, wasting the cooldown on a guaranteed
    // whiff until the target closes distance on its own. `targetDistance`
    // is null whenever nothing was actually selected (no qualifying
    // target, 'forward', or a 'cone' fallback) — treated as "not in
    // weapon range," failing closed the same way the NaN gate above it
    // does, not as "assume it's close enough."
    const targetInWeaponRange = aim.targetDistance !== null && aim.targetDistance <= weapon.current.range;
    if ((aim.enemyInRange && targetInWeaponRange) || aimPreset === 'forward') {
      const swing = weapon.tryFire(player.position, aim.dir, projectiles);
      // S07: `didFireLastCall` distinguishes "actually fired" from
      // "declined, cooldown not elapsed" — `tryFire`'s own `MeleeSwing |
      // null` return can't (both a decline AND a successful projectile fire
      // return null), so a plain `if (swing)` check here would miss every
      // projectile/pellets shot entirely.
      if (weapon.didFireLastCall) playSfx(audioBuses, 'shoot');
      // Melee has no travel time — S04's Stickball Bat resolves its swing
      // the instant it fires, unlike a projectile/pellets weapon which only
      // resolves later when the fired shot actually connects (below). The
      // arc is centered on `aim.dir`, NOT `player.getFacing()` — melee must
      // stay consistent with how the other two patterns already decouple
      // aim from body orientation (the whole point of S02's 3 assist
      // presets, Track X's own core lesson): a parked player under LOCK-ON
      // has their gun auto-aim at a target regardless of which way they're
      // stood, and a melee swing that only ever obeyed movement-facing would
      // silently whiff the exact enemy the crosshair-equivalent just locked.
      if (swing) {
        const meleeFacing = Math.atan2(aim.dir.x, aim.dir.z);
        const meleeEvents = resolveMeleePlayerHits(swing, [...enemyManager.getEnemies(), ...denExtraEnemies()], player.position, meleeFacing, pickups, buildLootContext(), superMeter);
        for (const ev of meleeEvents) {
          superMeter.onHit();
          playSfx(audioBuses, ev.killed ? 'kill' : 'hit', ev.position, player.position);
          if (ev.killed) {
            registerHuntKill(ev.kind);
            if (ev.kind) markKilledOnce(ev.kind);
            if (ev.kind) registerKillStat(ev.kind); // Wave I review fix: Progress.stats.killsByKind
          }
        }
        if (meleeEvents.length > 0) triggerShake(0.08, 0.1);
        // S06: the boss has no shared-consumable resource to race over (a
        // swing can land on every enemy in its arc already), so this can run
        // unconditionally alongside the enemy resolution above with no
        // ordering requirement.
        const bossMeleeEvents = resolveMeleeHitsOnBoss(swing, engagedBoss(), player.position, meleeFacing);
        for (const ev of bossMeleeEvents) {
          superMeter.onHit();
          playSfx(audioBuses, ev.killed ? 'kill' : 'hit', ev.position, player.position);
        }
        if (bossMeleeEvents.length > 0) triggerShake(0.08, 0.1);
        if (bossMeleeEvents.some((e) => e.killed)) handleBossDefeated();
        // Wave D-1: RatKingBoss isn't an Enemy (ai/ratking.ts's own header
        // comment) — same parallel-not-shared-function split as Gridlock's
        // own combat/bossResolve.ts, via combat/ratkingResolve.ts instead.
        const ratKingLive = lairEncounter.arena.state === 'active' ? (lairEncounter.boss as RatKingBoss | null) : null;
        const ratKingMeleeEvents = resolveMeleeHitsOnRatKing(swing, ratKingLive, player.position, meleeFacing);
        for (const ev of ratKingMeleeEvents) {
          superMeter.onHit();
          playSfx(audioBuses, ev.killed ? 'kill' : 'hit', ev.position, player.position);
        }
        if (ratKingMeleeEvents.length > 0) triggerShake(0.08, 0.1);
      }
    }

    if (superPressed && superMeter.consume()) {
      // S11: the return value is now consumed (was previously discarded —
      // no per-kill sfx/feedback needed for a super blast beyond the single
      // shared 'superUse' cue below, but a rat/pigeon/etc. caught in the
      // blast must still count toward an active hunt).
      const superEvents = applySuperBlast(player.position, [...enemyManager.getEnemies(), ...denExtraEnemies()], pickups, buildLootContext());
      for (const ev of superEvents) {
        if (ev.killed) {
          registerHuntKill(ev.kind);
          if (ev.kind) markKilledOnce(ev.kind);
          if (ev.kind) registerKillStat(ev.kind); // Wave I review fix: Progress.stats.killsByKind
        }
      }
      const bossSuperEvent = applySuperBlastOnBoss(player.position, engagedBoss(), SUPER_CONSTANTS.radius, SUPER_CONSTANTS.damage, block.physics);
      if (bossSuperEvent?.killed) handleBossDefeated();
      // Wave D-1
      applySuperBlastOnRatKing(player.position, lairEncounter.arena.state === 'active' ? (lairEncounter.boss as RatKingBoss | null) : null, SUPER_CONSTANTS.radius, SUPER_CONSTANTS.damage);
      triggerShake(0.3, 0.25);
      playSfx(audioBuses, 'superUse');
    }
  }

  if (!hitstopActive) {
    const active = projectiles.update(dt);
    const hitEvents = resolveProjectileHitsOnEnemies(active, [...enemyManager.getEnemies(), ...denExtraEnemies()], projectiles, pickups, buildLootContext(), superMeter);
    for (const ev of hitEvents) {
      superMeter.onHit();
      playSfx(audioBuses, ev.killed ? 'kill' : 'hit', ev.position, player.position);
      if (ev.killed) {
        registerHuntKill(ev.kind);
        if (ev.kind) markKilledOnce(ev.kind);
        if (ev.kind) registerKillStat(ev.kind); // Wave I review fix: Progress.stats.killsByKind
      }
    }
    // S06: must run AFTER the enemies pass above (bossResolve.ts's own
    // header comment on `resolveProjectileHitsOnBoss` explains why — both
    // consume the same `active` array, and only this function guards
    // against a projectile the enemies pass already deactivated this tick).
    const bossHitEvents = resolveProjectileHitsOnBoss(active, engagedBoss(), projectiles, block.physics, player.position);
    for (const ev of bossHitEvents) {
      superMeter.onHit();
      playSfx(audioBuses, ev.killed ? 'kill' : 'hit', ev.position, player.position);
    }
    if (bossHitEvents.some((e) => e.killed)) handleBossDefeated();
    // K-1(a): "hubcap broken by shooting it down in flight" — player bullets
    // vs. the tracked hubcap (or any other `breakable` enemy projectile, if
    // one is ever added). Sets `hubcapBrokenThisResolve`, read by the
    // Gridlock tick block ABOVE on the NEXT tick (the same one-tick-lag
    // pattern this file already accepts for a same-tick kill, per
    // handleBossDefeated's own comment) to call `boss.reportHubcapBroken()`.
    if (resolveProjectileHitsOnBreakableProjectiles(active, projectiles)) hubcapBrokenThisResolve = true;
    // Wave D-1
    const ratKingHitEvents = resolveProjectileHitsOnRatKing(active, lairEncounter.arena.state === 'active' ? (lairEncounter.boss as RatKingBoss | null) : null, projectiles);
    for (const ev of ratKingHitEvents) {
      superMeter.onHit();
      playSfx(audioBuses, ev.killed ? 'kill' : 'hit', ev.position, player.position);
    }
    if (!playerDead) {
      const playerHit = resolveProjectileDamageOnPlayer(active, player.position, PLAYER_RADIUS, playerHealth, projectiles, player.state.invulnerable);
      if (playerHit) {
        triggerShake(0.12, 0.12);
        playSfx(audioBuses, 'hurt');
      }
      // Runs AFTER hit-resolution and reads each projectile's own `.active`
      // flag (see graze.ts) so a shot that just landed a real hit this same
      // tick can't ALSO double-count as a graze.
      resolveGrazeOnPlayer(active, player.position, PLAYER_RADIUS, superMeter);
    }
  }

  // Death must be resolved BEFORE loot collection runs this tick, and loot
  // collection must be skipped entirely while dead — S04 review finding: a
  // heart pickup that finishes falling/magnet-pulling in during the exact
  // tick a lethal hit lands would otherwise heal `playerHealth.hp` back
  // above 0 before this check ever saw it reach 0, silently cancelling the
  // death outright; on any LATER tick during the ~1.4s respawn window it
  // would instead show a live, healed HP bar under the "DOWN — respawning…"
  // banner (`player.position` stays frozen while dead — nothing moves it —
  // so a pickup already close enough to be magnet-pulling keeps closing the
  // last stretch and getting collected). Every sibling per-tick system
  // above (movement, jump, checkpoints/interact, combat) already gates on
  // `!playerDead` for the same reason; this was the one omission.
  if (!playerDead && playerHealth.hp <= 0) {
    playerDead = true;
    respawnTimer = RESPAWN_DELAY_SECONDS;
    hud.showBanner('DOWN — respawning...');
    triggerShake(0.4, 0.35);
  } else if (playerDead) {
    respawnTimer -= dt;
    if (respawnTimer <= 0) respawnPlayer();
  }

  // Gated on the FRESH `playerDead` the check above just set — skips loot
  // collection entirely on the tick death is detected AND every tick of the
  // respawn window after, closing both halves of the review finding above.
  if (!playerDead) {
    const collected = pickups.update(dt, block.physics, player.position, PLAYER_RADIUS);
    if (collected.length > 0) {
      // Review fix (LOW, correctness): a persistent-collectible "FOUND"
      // toast and a same-tick cosmetic-grant toast (fired inside
      // applyCollectedLoot below, via grantWearables) can both try to show
      // in one synchronous tick — Hud.showToast's own documented "latest
      // wins" contract means whichever fires SECOND survives. Running the
      // collectible loop FIRST means a rarer, more significant cosmetic
      // grant is what's left on screen if both land the same tick, not the
      // reverse.
      for (const d of collected) {
        if (d.kind === 'collectible' && d.persistentId && !save.progress.placedCollectibles[d.persistentId]) {
          save.progress.placedCollectibles[d.persistentId] = true;
          snapshotSuspendedRun();
          hud.showToast('📍 FOUND — a keepsake');
          playSfx(audioBuses, 'stash');
        }
      }
      applyCollectedLoot(collected, wallet, playerHealth, grantWearables);
      // I-3's HUD materials chip — updated HERE, immediately after the
      // grant that can move it from 0/0 to nonzero, and BEFORE the fly-arc
      // dispatch below. Review fix (MEDIUM, correctness): the chip used to
      // stay unhidden until the later, tick-end hud.setMaterials() call, so
      // the very first material/part fly-arc of a session (or any fly-arc
      // right after both materials were spent to exactly 0) read the
      // still-`display:none` chip's all-zero getBoundingClientRect() and
      // animated to the viewport's top-left corner instead of the chip.
      hud.setMaterials(wallet.materials.scrap, wallet.materials.part);
      // Review fix (HIGH, spec-fidelity): Progress.stats — I-3's own line
      // names durable lifetime coin/scrap/part/heart/drop counters as a
      // required deliverable, distinct from sessionLog's per-session delta.
      const stats = save.progress.stats;
      for (const d of collected) {
        if (d.kind === 'coin') stats.coins++;
        else if (d.kind === 'material') stats.scrap++;
        else if (d.kind === 'part') stats.parts++;
        else if (d.kind === 'heart') stats.hearts++;
      }
      stats.drops += collected.length;
      if (collected.some((d) => d.kind === 'heart')) sessionLog.hearts += collected.filter((d) => d.kind === 'heart').length; // I-3's [THIS RUN] log
      // Wave L-4: a cosmetic batch's own combined toast/sfx (grantWearables)
      // already fires above — that cue would be redundant noise on top of
      // 'rareDrop' the same tick. A persistent collectible gets its own
      // 'stash' cue right above. Everything else (coin/material/heart/part)
      // gets I-1's rising arpeggio instead of the old flat single blip.
      const economyDrops = collected.filter((d) => d.kind !== 'cosmetic' && d.kind !== 'collectible');
      if (economyDrops.length > 0) {
        playPickupArpeggio(audioBuses, economyDrops.length);
        // I-1: "fly-to-chip DOM arc (batched '×N', cap 6)" — one arc per
        // KIND present this tick (not one per individual drop), each
        // launched from the position of whichever of that kind's drops
        // happened to be first in the array (good enough — several same-
        // tick drops of one kind land within a fraction of a meter of each
        // other, per pickup.ts's own SPAWN_XZ_JITTER).
        const byKind = new Map<LootKind, { count: number; position: THREE.Vector3 }>();
        for (const d of economyDrops) {
          const entry = byKind.get(d.kind);
          if (entry) entry.count++;
          else byKind.set(d.kind, { count: 1, position: d.position });
        }
        for (const [kind, info] of byKind) {
          const screen = worldToScreen(info.position);
          hud.spawnFlyArc(screen.x, screen.y, kind, info.count);
        }
      }
    }
  }

  // A one-shot cue on the exact tick super crosses into ready — gotcha #30's
  // crossing-cue law (every gain source must share ONE cue, not just the
  // sites that happen to remember it). S07 closes the "audio is S07's job"
  // placeholder this used to be: a real chime alongside the shake, not a
  // replacement for it — both are established feedback channels in this
  // project (Classic used a sound AND a visual cue for the same moment).
  if (superMeter.justCrossedReady()) {
    triggerShake(0.05, 0.15);
    playSfx(audioBuses, 'superReady');
  }

  if (shakeT > 0) shakeT = Math.max(0, shakeT - dt);

  // S07: periodic suspend snapshot — see snapshotSuspendedRun's own comment.
  snapshotTimer += dt;
  if (snapshotTimer >= SNAPSHOT_INTERVAL_SECONDS) {
    snapshotTimer = 0;
    snapshotSuspendedRun();
  }

  hud.update(playerHealth.hp, playerHealth.maxHp, superMeter.value, SUPER_CONSTANTS.max, aimPreset, weapon.current, wallet.coins);
  hud.setMaterials(wallet.materials.scrap, wallet.materials.part); // I-3's HUD materials chip
  // S11 review fix (HIGH, spec-fidelity): a persistent live readout for
  // PLAN-CITY §5's "pick target -> tracked" — the hunt board (bodega-only)
  // and the one-shot completion toast were the ONLY places progress ever
  // showed before this. Recomputed every tick from the live save, same
  // "read straight off state, no separate tracked copy" discipline as
  // `hud.update`'s own params.
  //
  // Wave I (I-4) review-anticipated fix: PLAN-CITY3 §10's own G5 checklist
  // demands "the objectives list always tells him what to do next — the
  // game never goes silent when a hunt ends." The old `: null` branch did
  // exactly that (blank pill) the moment a hunt finished until the player
  // walked back to the board — this falls back to the next undone task
  // instead, only truly going quiet once every task is done.
  const activeHunt = save.progress.activeHunt;
  if (activeHunt) {
    hud.setHuntProgress(`🎯 ${huntLabelFor(activeHunt.kind)} ${activeHunt.killedCount}/${activeHunt.targetCount}`);
  } else {
    const next = nextUndoneTask(buildTasks(save.progress, save.wardrobe));
    hud.setHuntProgress(next ? `📋 ${next.label}` : '🏆 All objectives complete');
  }

  // T3-1 review fix (HIGH, spec-fidelity lens): `insideBodega` is written at
  // exactly 5 sites (boot, S07 resume, enter_bodega, exitBodegaToStreet —
  // shared by the EXIT button and the walk-out threshold check below — and
  // this invariant itself) — but the
  // player's ACTUAL position can also change via two paths that know
  // NOTHING about this flag: PlayerCapsule's own -20m "fell off the map"
  // safety net (world/controller.ts, fires when a player walks past T3-1's
  // own stoop — interior.ts's own comment: "a catch, not a wall") and
  // respawnPlayer()'s on-death checkpoint teleport above. Both always land
  // EXTERIOR. Left unguarded, either one leaves `insideBodega` stuck `true`
  // while the player stands outside — silently reproducing BOTH symptoms
  // T3-1 exists to fix (the interior floating, visible from the street, and
  // an unnaturally tight indoor camera clamp outdoors). A cheap, self-
  // healing invariant — checked once per tick, correct regardless of WHICH
  // code path moved the player, including any future one — beats hooking
  // every individual teleport call site by name. 50 is a wide, safe margin:
  // the interior room + stoop never extend past x≈141.3 (interior.ts), so
  // anything this far from INTERIOR_ORIGIN.x (140) is unambiguously outside.
  // T3-1 follow-up (Karan's post-T3 phone check: "you can't really just walk
  // right out the door — you kind of fall out the door"): walking out the
  // doorway now IS the exit. The moment the player's center clears the south
  // wall's outer face, run the exact routine the EXIT BODEGA button runs —
  // one seamless cut to the sidewalk, this same tick, before anything
  // renders the player standing in the isolated interior origin's open air.
  // See isPastDoorwayThreshold's own comment (world/interior.ts) for the two
  // real-play gaps the stoop+curb version left open. Checked before the
  // self-healing invariant below so a genuine walk-out exits HERE (landing
  // at the standpoint), never via the invariant's recover-only path.
  if (insideBodega && isPastDoorwayThreshold(player.position.x, player.position.z)) {
    exitBodegaToStreet();
    interactGraceTimer = INTERACT_GRACE_AFTER_AUTO_TRANSITION; // auto transitions only — the button paths never set this
  }

  // T3-1 follow-up 2 (Karan's re-check: "that worked but once i leave i
  // can't get back inside"): the walk-out fix made doors walkable, so the
  // exterior door is now symmetric — pushing INTO the door face walks you
  // in, same routine as the ENTER button (which stays as a redundant
  // path). Two-part trigger: `isAtBodegaDoorFace` (layout.ts) is a real 3D
  // box at the door's street face (review fix, HIGH: the first cut was an
  // unbounded column that crossed the walkable rooftop 16m above), and the
  // intent gate reads the player's DESIRED movement direction (what
  // they're pushing, not what the collider let them achieve): mostly
  // toward the door (>0.5 of the direction's length in +z, i.e. within
  // ~60° of straight-at-it). A position-delta gate was tried first and
  // review-confirmed to dead-lock the exact promised gesture — a player
  // who ARRIVES at the door pinned by the collider (wall-slide approach)
  // has zero achieved motion forever, while intent reads their push
  // regardless. A pure strafe across the storefront has ~zero desired-z
  // (no fire); a teleport with no input held has zero desired direction
  // (no fire); a knockback landing a player in the box only enters if
  // they're also pushing at the door — otherwise it never fires.
  const desiredLen = Math.hypot(player.lastDesiredDirX, player.lastDesiredDirZ);
  if (
    !playerDead && !insideBodega &&
    isAtBodegaDoorFace(player.position.x, player.position.y, player.position.z) &&
    desiredLen > 0.05 && player.lastDesiredDirZ / desiredLen > 0.5
  ) {
    enterBodega();
    interactGraceTimer = INTERACT_GRACE_AFTER_AUTO_TRANSITION; // an in-flight ENTER tap must not fire EXIT from inside (same class as the exit-side grace)
  }

  if (insideBodega && Math.abs(player.position.x - INTERIOR_ORIGIN.x) > 50) {
    insideBodega = false;
    block.setInteriorVisible(false);
  }

  // B-2 "Enterable interiors" (PLAN-CITY2 §3) — the laundromat's own
  // walk-out/walk-in pair, mirroring the bodega's two checks above exactly
  // (same threshold-then-door-face ordering, same grace-timer usage — a
  // single `interactGraceTimer` already guards every auto transition in this
  // file, laundromat included, since only one interior can be "just
  // transitioned into/out of" on any given tick).
  if (insideLaundromat && isPastLaundromatDoorwayThreshold(player.position.x, player.position.z)) {
    exitLaundromatToStreet();
    interactGraceTimer = INTERACT_GRACE_AFTER_AUTO_TRANSITION;
  }
  const laundromatDesiredLen = Math.hypot(player.lastDesiredDirX, player.lastDesiredDirZ);
  if (
    !playerDead && !insideLaundromat &&
    isAtLaundromatDoorFace(player.position.x, player.position.y, player.position.z) &&
    laundromatDesiredLen > 0.05 && player.lastDesiredDirZ / laundromatDesiredLen > 0.5
  ) {
    enterLaundromat();
    interactGraceTimer = INTERACT_GRACE_AFTER_AUTO_TRANSITION;
  }
  if (insideLaundromat && Math.abs(player.position.x - LAUNDROMAT_ORIGIN.x) > 50) {
    insideLaundromat = false;
    block.setLaundromatVisible(false);
  }

  // B-2: the basement has no walk-through door (interiorBasement.ts's own
  // header comment) so it needs only the self-healing recovery invariant,
  // not a threshold/door-face pair — the ONLY way in or out is the
  // `enter_basement`/`exit_basement` interact actions above, both of which
  // already keep the flag and `basementGroup`'s visibility in lock-step.
  // This still matters as a backstop: PlayerCapsule's own -20m safety net
  // (world/controller.ts) always recovers to an EXTERIOR position, exactly
  // the same unforeseen-path risk `insideBodega`'s own invariant guards.
  if (insideBasement && Math.abs(player.position.x - BASEMENT_ORIGIN.x) > 50) {
    insideBasement = false;
    block.setBasementVisible(false);
  }

  // Wave D-1: the lair's own walk-out check, mirroring the bodega's own
  // threshold-then-door-face ordering (T3-1) — see world/denLair.ts's own
  // `isPastLairDoorwayThreshold` comment for the exact derivation.
  if (insideLair && isPastLairDoorwayThreshold(player.position.x, player.position.z)) {
    exitLairToTunnel();
    interactGraceTimer = INTERACT_GRACE_AFTER_AUTO_TRANSITION;
  }
  // K-2/L5 review fix (HIGH, confirmed by all 3 lenses): the old walk-through
  // +directional-intent auto-teleport into the lair (isAtTunnelBreachFace +
  // a dot-product intent check -> enterLair()) lived here and was supposed
  // to have been DELETED when the tunnel breach became a real INTERACT
  // prompt (world/interact.ts's `lair_entrance`) — it never actually was,
  // so both mechanisms were live at once and the old one fired first,
  // making the new prompt practically unreachable in normal play. Entry is
  // now handleInteract()'s 'enter_lair' case ONLY (dispatched from
  // hud.consumeInteractPress(), same as bodega/laundromat/basement).
  // Fix session (HIGH, corroborated by all 3 lenses): this self-heal never
  // called `lairEncounter.onExit()`/`hud.hideBossBar()` — a death inside the
  // lair (respawnPlayer() teleports to an exterior checkpoint, >50m away)
  // left DenArena stuck 'active' with the Rat King at whatever HP it had.
  // Re-entering was then a no-op (`onEnter()` only fires from 'dormant'), so
  // the fight resumed exactly where it left off — a free "keep the boss's
  // partial HP across a death" exploit, and the exact attrition case D-4's
  // leave-resets-the-fight rule exists to close. Mirrors `exitLairToTunnel()`
  // exactly (both are "the player is no longer meaningfully inside the lair"
  // moments), just reached via an unforeseen-path teleport instead of the
  // walk-out door.
  if (insideLair && Math.abs(player.position.x - LAIR_ORIGIN.x) > 50) {
    insideLair = false;
    block.setLairVisible(false);
    lairEncounter.onExit();
    hud.hideBossBar();
  }

  // Wave D-3: the sewer den's own walk-out check.
  if (insideSewer && isPastPipeEntryThreshold(player.position.x, player.position.z)) {
    exitSewerToStreet();
    interactGraceTimer = INTERACT_GRACE_AFTER_AUTO_TRANSITION;
  }
  // K-2/L5 review fix (HIGH, confirmed by all 3 lenses): same leftover-old-
  // path bug as the lair above — the walk-through+"pushing toward the
  // hole's center" auto-teleport into the sewer den (isAtTrapHoleFace +
  // isPushingTowardTrapHole -> enterSewer()) lived here and was never
  // actually deleted when the trap hole became a real INTERACT prompt
  // (world/interact.ts's `sewer_entrance`). Entry is now handleInteract()'s
  // 'enter_sewer' case ONLY.
  // Fix session: same HIGH fix as the lair's own self-heal above — dying
  // inside the sewer den left its DenArena stuck 'active' too.
  if (insideSewer && Math.abs(player.position.x - SEWER_ORIGIN.x) > 50) {
    insideSewer = false;
    block.setSewerVisible(false);
    sewerEncounter.onExit();
  }
}

const loop = new GameLoop({
  update: tick,
  render: () => {
    overlay?.recordFrame();
    if (shakeT > 0) {
      const amt = shakeStrength * (shakeT / shakeDuration);
      orbitCam.camera.position.x += (Math.random() - 0.5) * amt;
      orbitCam.camera.position.y += (Math.random() - 0.5) * amt;
    }
    renderer.render(scene, orbitCam.camera);
    overlay?.render();
  },
});
loop.start();

// Dev-only debug hook, mirrors Classic's javascript_tool headless-probe
// convention (gotcha #3 family) — used by harness/run.mjs and by hand during
// verification. Zero footprint when ?dev=1 is absent.
//
// Stable keys harness/run.mjs actually depends on — changing these shapes
// requires updating the harness in the same change, since a renamed/removed
// field fails a check silently rather than throwing:
//   renderer.info.render.{calls,triangles}, player.position.{x,y,z},
//   player.state.{grounded,sliding,vaulting,invulnerable},
//   player.update(dt,move,forward,right), player.teleport(x,z), player.mesh,
//   camera.position, playerHealth.{hp,maxHp,invulnerableT},
//   enemyManager.getEnemies(), projectiles.activeCount(),
//   superMeter.{value,ready}, getAimPreset/setAimPreset, weapon.tryFire
//   (several checks override then `delete` it to restore the prototype
//   method), resolveAimDirectionRaw(preset, playerPos, facing, targets) —
//   returns {dir, enemyInRange, targetDistance} (was a bare Vector3 before
//   T2 added enemyInRange; S04's review added targetDistance — null unless
//   a real target was selected) — the harness's aim checks read `.dir` and
//   assert the flags.
//   T1 added: orbitCam (applyLook(dx,dy,sensitivity), update(target,dt) —
//   dt defaults to 0, so a legacy one-arg probe call freezes the drag-grace
//   timer for that tick (it neither decays nor NaN-poisons; an earlier
//   build without the default let `timer - undefined` produce NaN, which
//   disabled auto-follow permanently) — followMovement(moveX,moveY,dt),
//   MAX_FOLLOW_RATE (exported const, the harness's rate-bound checks),
//   getForward()/getRight(), snapTo(target));
//   input (input.move is written directly by real keyboard events the
//   harness dispatches, not by the harness itself).
//   S04 added: pickups (PickupPool — spawn(kind,origin), activeCount(),
//   clearAll(), update(dt,physics,playerPos,radius) returning collected
//   LootKind[]), wallet ({coins, materials:{scrap}}), getWeaponId/
//   setWeaponId(id) (mirrors getAimPreset/setAimPreset), weapon.currentId /
//   weapon.tryFire(originXZ,aimDir,pool) returning a MeleeSwing or null.
//   S05 added: checkpoints (CheckpointTracker — `.current` {id,label,
//   respawnX,respawnZ}, `.update(x,z)` returning the newly-activated
//   Checkpoint or null, same "call directly with synthetic positions"
//   convention S03's habitat/nav checks already use) and interact
//   (InteractSystem — `.update(x,z)` returning the nearest in-range
//   Interactable or null). A harness check exercises the REAL interact
//   button the same way it already does for super (`page.locator(
//   '.br3d-interact-btn').click()`), not a synthetic hook call, so it
//   covers the actual `hud.consumeInteractPress()` -> `handleInteract`
//   wiring, not just the systems in isolation.
//   S07 added: save (the SAME live SaveData `loadSave()` returned at boot —
//   every setter in this file mutates THIS object, never a copy) and
//   saveModule ({loadSave,salvageSave,writeSave} — the raw functions, same
//   "expose the pure function for direct testing" convention as
//   resolveAimDirectionRaw); getUiState/setUiState (UiState =
//   'playing'|'paused'|'settings', the single source of truth tick()'s own
//   top gates on) and applySettingsPatch(partial<Settings>) (the same
//   function PauseMenu's callbacks use — a check can drive a settings
//   change exactly the way a real player would via the panel); pauseMenu
//   (the PauseMenu instance — its own DOM is also exercised as REAL button
//   clicks via `page.locator('.br3d-pause-btn')` etc., not just this hook);
//   audioBuses (AudioBuses — `.isUnlocked`, `.lastSfxPlayed` is the sfx
//   verification seam: `audio/sfx.ts`'s `playSfx()` sets it unconditionally
//   before doing any real WebAudio work, so a check can confirm "did the
//   right cue fire, positional or not" without inspecting audio node
//   graphs) and music (MusicSequencer — `.isPlaying`); snapshotSuspendedRun
//   (forces an immediate suspend-snapshot write instead of waiting out the
//   real 4s periodic timer); getShakeT/triggerShakeTest (the only way to
//   observe/drive `shakeT`/`triggerShake`, both otherwise closed over this
//   module's private scope, needed to test the shakeEnabled toggle).
//   S11 added: huntBoard (ui/huntboard.ts instance — `setVisible` is also
//   exercised via the real `setUiState('huntboard')` transition, same
//   "test the actual wiring, not just the isolated class" bar as pauseMenu
//   above) and the three Scrap Armor tuning constants (ARMOR_BONUS_HP/
//   ARMOR_COST_SCRAP/ARMOR_COST_COINS), exposed rather than hand-duplicated
//   in the harness. `save.progress.activeHunt`/`.armorCrafted`/
//   `.hasPlayedBefore`/`.huntsCompleted` are all reachable through the
//   existing `save`/`saveModule` pair above — no new hook needed for them.
//   `isFirstRun` is a one-time boot snapshot (not a live getter — it can't
//   change after module init) for a harness check driving a genuinely fresh
//   boot via an isolated `browser.newContext()` (S07's own documented
//   page.reload()-isn't-a-fresh-boot lesson applies identically here).
//
// `tick(dt)` is exposed so a probe can manually step fixed updates when the
// real rAF loop isn't ticking — same "drive it by hand" pattern Classic's
// own headless verification uses whenever the pane isn't actually visible
// to a viewer (rAF silently never fires while `document.hidden`). The T1
// camera-follow checks specifically rely on driving REAL held keys through
// this — see harness/run.mjs's own comment on `driveHeldMove` for why a
// probe that hand-feeds `orbitCam.followMovement` a fixed vector instead
// cannot catch a feedback-loop regression in the production wiring.
if (devMode) {
  (window as unknown as { __BR3D__: Record<string, unknown> }).__BR3D__ = {
    scene,
    renderer,
    camera: orbitCam.camera,
    orbitCam,
    maxFollowRate: MAX_FOLLOW_RATE, // rad/s — the harness's camera rate-bound checks read the live value instead of hardcoding it
    // B-1 review finding (CONFIRMED, spec-fidelity/dont-break-list): these
    // were exported from layout.ts "so the harness can assert" reachability,
    // but nothing ever actually read them anywhere — a future MOVE_SPEED/
    // JUMP_SPEED/GRAVITY/VAULT_MAX_HEIGHT tuning change could silently break
    // B-1's rooftop network with zero compile-time or in-app signal. Exposed
    // here so the harness's own B-1 checks can assert them directly instead
    // of only re-deriving reachability through an independent end-to-end
    // physics simulation (which, per a companion review finding, can itself
    // pass by accident near a knife's-edge margin).
    rooftopGapMaxReach: ROOFTOP_GAP_MAX_REACH,
    rooftopGapActual: ROOFTOP_GAP_ACTUAL,
    rooftopVantageRequiresJump: ROOFTOP_VANTAGE_REQUIRES_JUMP,
    rooftopVantageClearanceMargin: ROOFTOP_VANTAGE_CLEARANCE_MARGIN,

    player,
    input,
    block,
    navGrid,
    tick,
    playerHealth,
    enemyManager,
    projectiles,
    weapon,
    superMeter,
    // S04 additions — pickups (PickupPool.spawn/activeCount/clearAll, `.update`
    // returning collected LootKind[]), wallet ({coins, materials.scrap}, mutated
    // only through economy/wallet.ts's grant/spend choke functions), and
    // weapon-id get/set (mirrors getAimPreset/setAimPreset's pair exactly).
    pickups,
    wallet,
    getWeaponId: () => weapon.currentId,
    setWeaponId: (id: WeaponId) => {
      setWeaponAndPersist(id);
    },
    // S05 additions — checkpoints.current/update(x,z) and
    // interact.update(x,z), both callable directly with synthetic positions
    // for a harness probe. The interact BUTTON itself is exercised as a
    // real DOM click (see the header comment above), not via this hook.
    checkpoints,
    interact,
    isPlayerDead: () => playerDead,
    forceRespawnNow: () => {
      playerHealth.hp = 0;
      playerDead = true;
      respawnTimer = 0.001;
    },
    getAimPreset: () => aimPreset,
    setAimPreset: (p: AimPreset) => {
      setAimPresetAndPersist(p);
    },
    // The raw pure function (not a closure over live enemy state) — the
    // harness calls this with fully synthetic player/target positions so an
    // aim-preset test never depends on where real spawned enemies happen to
    // be, or on leftover player facing from an earlier check.
    resolveAimDirectionRaw: resolveAimDirection,
    // Same "expose the raw function against a synthetic projectile array"
    // idiom as resolveAimDirectionRaw above — lets the harness deterministically
    // test the graze band without needing a real enemy to actually fire a lob.
    resolveGrazeRaw: resolveGrazeOnPlayer,
    // Lets the harness resolve a melee swing deterministically (given a
    // `MeleeSwing` from `weapon.tryFire`) instead of depending on the real
    // per-frame `aim.enemyInRange` gate to line up during a real-time wait.
    resolveMeleePlayerHitsRaw: resolveMeleePlayerHits,
    // S09: same "expose the raw pure function" idiom, not a dynamic
    // `import('/src/world/interior.ts')` — that path only resolves against
    // Vite's DEV transform; the harness serves the built `dist/` output,
    // where individual source files aren't servable and the import 404s
    // (confirmed live: it fatally crashed the whole harness run, not just
    // one check). Lets a check compare the real C04 counter mesh's world
    // position against the SAME authoritative formula collision.ts uses.
    interiorCounterCollider,
    // S07 additions — save/settings/pause/audio. `save` is the SAME live
    // object every setter above mutates (never a copy) so a harness check
    // can both read current settings and confirm a change actually
    // persisted to localStorage via `saveModule.loadSave()` reading it back
    // fresh. `saveModule` exposes the raw salvage/load/write functions
    // (same "expose the pure function for direct testing" convention as
    // `resolveAimDirectionRaw` above) so a check can throw synthetic
    // malformed JSON at `salvageSave` without needing a real corrupt
    // localStorage value.
    save,
    saveModule: { loadSave, salvageSave, writeSave },
    getUiState: () => uiState,
    setUiState: (s: UiState) => setUiState(s),
    applySettingsPatch,
    pauseMenu,
    audioBuses,
    music,
    snapshotSuspendedRun,
    // Test seam for the shake/flash accessibility toggles — neither
    // `shakeT` nor `triggerShake` is otherwise reachable from outside this
    // closure.
    getShakeT: () => shakeT,
    triggerShakeTest: (strength: number, duration: number) => triggerShake(strength, duration),
    // S06 additions — `bossArena` is a stable object reference (safe to
    // expose directly, same as enemyManager/checkpoints/interact above);
    // `boss` itself is a reassigned `let` (null until its GLB resolves), so
    // it needs a live getter — a plain `boss,` property would freeze at
    // whatever it was at debug-hook-construction time (always null, since
    // this object builds synchronously before the async load resolves),
    // same reason getWeaponId/getAimPreset above are getters, not values.
    bossArena,
    getBoss: () => boss,
    // K-1(a) review fix regression coverage: exposes the tracked hubcap
    // projectile so a harness check can fire a real player shot at its
    // LIVE position and confirm the full main.ts tick()->reportHubcapBroken()
    // ordering (not just the isolated Boss FSM, which tools/test-gridlock-
    // fsm.ts already covers and which structurally cannot exercise this
    // integration-level one-tick-lag bug on its own).
    getActiveHubcap: () => activeHubcap,
    hud,
    resolveMeleeHitsOnBossRaw: resolveMeleeHitsOnBoss,
    // S08 additions — gamepad (GamepadInput.poll(), same GamepadFrame shape
    // every tick reads) and quality-tier live switching (defaults to
    // device-detected; a settings-panel row can call setQualityTier whenever
    // Karan wants one added).
    gamepadInput,
    setQualityTier,
    getQualityTier: () => quality.tier,
    // S11 additions — huntBoard (setVisible is also exercised via the real
    // 'huntboard' uiState transition, same "test the actual wiring" bar as
    // every other UI screen in this file); the three Scrap Armor constants,
    // exposed rather than hand-duplicated as literals in the harness, so a
    // future rebalance can't silently desync the check from the real cost;
    // isFirstRun (a boot-time snapshot, read-only — a harness check drives a
    // GENUINELY fresh boot via an isolated browser context, per S07's own
    // documented page.reload()-isn't-a-fresh-boot lesson, and reads this to
    // confirm the flow actually ran on that boot).
    huntBoard,
    isFirstRun,
    ARMOR_BONUS_HP,
    ARMOR_COST_SCRAP,
    ARMOR_COST_COINS,
    // Wave L-3 additions — the bench's next-tier constants, exposed the same
    // "don't hand-duplicate a literal in the harness" way ARMOR_* already are.
    RIOT_PLATING_COST_PARTS,
    RIOT_PLATING_COST_COINS,
    RIOT_PLATING_BONUS_HP,
    SALVAGE_EXCHANGE_COST_PARTS,
    SALVAGE_EXCHANGE_GAIN_COINS,
    SALVAGE_EXCHANGE_GAIN_SCRAP,
    // S12 gate-pass addition — a getter (not a value) since `overlay` is a
    // reassigned `let`, same reason `getBoss` above is a getter. Without
    // `?dev=1` this whole `__BR3D__` object is never assigned at all (see
    // this function's own call site), so in practice this getter only ever
    // returns `null` for the one frame before the overlay's first
    // `render()` call, never because of a missing `?dev=1` — the `?? null`
    // is `getSnapshot()`'s own pre-first-render case, not a dev-mode gate.
    // Lets a scripted soak (this session's own 20-minute stability pass, or
    // any future one) read the SAME peak-tracked numbers the on-screen
    // overlay shows, as data instead of parsed display text.
    getOverlaySnapshot: () => overlay?.getSnapshot() ?? null,
    // Wave L-1/L-2/L-3 additions — playerVisual (PlayerVisual.ready/
    // getEquipped/equip, plus `.group` for a harness check to confirm the
    // real GLB loaded and the capsule was hidden); wardrobeScreen (setVisible
    // also exercised via the real `setUiState('wardrobe')` transition, same
    // bar as huntBoard/pauseMenu above); buildLootContext/markKilledOnce/
    // grantWearable (the raw functions, same "expose for direct testing"
    // convention as resolveAimDirectionRaw) so a check can grant/roll loot
    // deterministically without needing a real kill to land.
    playerVisual,
    wardrobeScreen,
    buildLootContext,
    markKilledOnce,
    grantWearable,
    grantWearables,
    // Wave I additions (PLAN-CITY3 §5) — stashScreen (setVisible also
    // exercised via the real `setUiState('stash')` transition, same bar as
    // every sibling screen above); buildTasks/nextUndoneTask (the raw
    // meta/tasks.ts functions, same "expose for direct testing" convention);
    // getSessionLog (the live [THIS RUN] log, same live-object convention as
    // `save` above — a check can read it directly rather than re-deriving
    // deltas itself); worldToScreen (the raw projection helper, so a check
    // can verify a fly-arc launches from the right screen point without
    // hand-rolling the same NDC math); grantWearableDirect (the raw
    // function, same convention as grantWearable — lets a check exercise
    // L6's guaranteed-cosmetic path without a real kill); playSfxRaw (the
    // raw audio/sfx.ts function — not otherwise reachable from the debug
    // hook, since every existing call site is buried inside gameplay logic
    // — lets a check confirm 'craft'/'stash'/'huntComplete' are real,
    // independently distinct cues without walking to a physical location);
    // registerHuntKillRaw (the raw function — lets a check drive a hunt
    // completion deterministically to verify the "names the next step"
    // toast composition, without a real kill).
    stashScreen,
    buildTasks,
    nextUndoneTask,
    getSessionLog,
    worldToScreen,
    grantWearableDirect,
    playSfxRaw: playSfx,
    registerHuntKillRaw: registerHuntKill,
    // Wave L-1 additions — the raw drop-rolling functions + the seeded RNG's
    // own test-reset hook, same "expose the pure function for direct
    // testing" convention as resolveAimDirectionRaw/resolveGrazeRaw above —
    // lets a check drive a deterministic roll (and, critically, verify the
    // KillLootContext.ownedWearables same-batch-mutation anti-dupe fix)
    // without needing a real kill or a lucky low-probability RNG roll.
    rollEnemyExtras,
    rollBossExtras,
    resetDropsRngForTest: __resetDropsRngForTest,
    // Wave D additions — the two den encounters (stable object references,
    // same as `bossArena` above; `.boss`/`.elite` are mutable fields the
    // harness can poke directly, same "reach into private-by-TS-only state"
    // convention this file's own boss checks already use), their own
    // enter/exit routines + walk-trigger predicates (raw functions, same
    // convention as `resolveAimDirectionRaw`), and the raw geometry the
    // harness needs to place a synthetic player/probe without hand-copying
    // literals that could drift from the real data.
    lairEncounter,
    sewerEncounter,
    enterLair,
    exitLairToTunnel,
    enterSewer,
    exitSewerToStreet,
    isAtTunnelBreachFaceRaw: isAtTunnelBreachFace,
    isAtTrapHoleFaceRaw: isAtTrapHoleFace,
    resolveMeleeHitsOnRatKingRaw: resolveMeleeHitsOnRatKing,
    resolveProjectileHitsOnRatKingRaw: resolveProjectileHitsOnRatKing,
    denExtraEnemies,
    LAIR_ORIGIN,
    LAIR_SPAWN,
    TUNNEL_BREACH_CENTER,
    TUNNEL_BREACH_STANDPOINT,
    LAIR_ENTRANCE_MARKER,
    SEWER_ORIGIN,
    PIPE_ENTRY,
    TRAP_HOLE_POS,
    TRAP_HOLE_STANDPOINT,
    isPushingTowardTrapHoleRaw: isPushingTowardTrapHole,
    isPastPipeEntryThresholdRaw: isPastPipeEntryThreshold,
    GRIDLOCK_ARENA,
    GRIDLOCK_WRECKS,
    GRIDLOCK_BARRIER_NORTH,
    GRIDLOCK_BARRIER_SOUTH,
    LAIR_PILLARS,
  };
}
